/**
 * Outlets by Time — exec-only outlet profitability by hour of day.
 *
 * Gated on SG-Exec-Reports. The tab is not rendered at all unless me.isExec,
 * and every endpoint behind it re-checks the group server-side, so hiding the
 * button is presentation only, not the security boundary.
 *
 * Layout, top to bottom:
 *   1. Costs      — Retail and Tea Room panels: Cost of Goods / Staffing /
 *                   Utilities / Maintenance as percentages, with the profit
 *                   margin auto-calculated as the remainder. Per-outlet
 *                   overrides sit inside each panel.
 *   2. Item costs — top items by sales with MERAC's real cost, editable by
 *                   margin % or by cash cost.
 *   3. Outlets    — one tab per outlet. Monthly sales, a breakeven margin bar
 *                   chart, then sales & profit by hour for each month, and a
 *                   day drill-down under any month.
 *   4. Summary    — all outlets combined, both chart types.
 *   5. Losses     — for a chosen month, the days and hours running at a loss.
 *
 * The profit arithmetic lives in execOutletsModel.ts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiDayRow,
  ApiSeason,
  Bucket,
  CogsSource,
  CostModelRow,
  DayModel,
  FISCAL_MONTHS,
  MonthModel,
  OutletModel,
  SlotBucket,
  StaffSource,
  StaffCostRow,
  OptimiseResult,
  LeanStaffingResult,
  buildDayModels,
  buildOutletModels,
  categoryHasActualCost,
  defaultStaffCount,
  fiscalMonthLabel,
  foldSlots,
  foldedSlotLabel,
  leanStaffing,
  marginPct,
  modelRate,
  optimiseOutlet,
  money,
  moneyPence,
  outletStandings,
  pct,
  runScenario,
  slotLabel,
  slotShortLabel,
  standingLabel,
  staffingRag,
  StaffingRag,
  LOSS_THRESHOLD_PER_MONTH,
} from "./execOutletsModel";
import { exportExecPdf, exportOptimiserPdf } from "./execOutletsPdf";
import "./exec-outlets.css";

// ---------------------------------------------------------------------------
//  Item costs API shape
// ---------------------------------------------------------------------------

interface ItemRow {
  plu: string;
  item: string | null;
  analysis: string | null;
  qty: number;
  net: number;
  actual_cost: number;
  actual_unit_cost: number | null;
  effective_cost: number;
  margin: number;
  margin_pct: number | null;
  coverage_pct: number | null;
  override_mode: "cost" | "margin" | null;
  override_value: number | null;
}

// ---------------------------------------------------------------------------
//  Live Rostering — cross-service staff cost/hours feed
//
//  /api/dashboards/exec/rostering-labour returns, per mapped outlet, the season's
//  rostered labour from rostering-nymr. `cost` is EMPLOYED-ONLY — a rostered
//  volunteer contributes hours but £0 (applied on the rostering side). The tab
//  uses this to split staff out of the modelled "fixed" pot on the Sales-by-month
//  table: staff = this rostered figure, fixed = utilities + maintenance only.
// ---------------------------------------------------------------------------

interface RLStaff {
  personId: number | string;
  name: string;
  roles: string[];
  shifts: number;
  hours: number;
  /** employed-only £ — a volunteer's hours cost nothing */
  cost: number;
}
interface RLTotals {
  staffCount: number;
  shifts: number;
  hours: number;
  cost: number;
}
interface RLOutlet {
  category: string;
  location: string;
  unitId: string;
  available: boolean;
  staff: RLStaff[];
  totals: RLTotals;
  hasUnratedRoles: boolean;
  error?: string;
}
interface RLFeed {
  available: boolean;
  configured: boolean;
  mode?: "actual" | "forecast";
  fiscalYear?: number;
  from?: string;
  to?: string;
  currency?: string;
  outlets: RLOutlet[];
  error?: string;
}

/**
 * The per-scope view of the rostering feed the Sales-by-month table needs.
 * Works for a single outlet and for a summary (a set of outlets combined).
 *   staffByMonth      — the season's rostered £ apportioned across fiscal months
 *                       pro-rata by that month's net (mirrors how iplicit staff
 *                       is spread), employed-only.
 *   otherFixedByMonth — the NON-staff fixed pot (utilities + maintenance +
 *                       management), struck exactly as the base model does
 *                       (per trading day), so fixed + staff reconciles.
 */
interface RosterOverlay {
  available: boolean;
  mode: "actual" | "forecast";
  staffTotal: number;
  staffByMonth: Map<number, number>;
  otherFixedByMonth: Map<number, number>;
  otherFixedTotal: number;
  staff: RLStaff[];
  totals: RLTotals;
  hasUnratedRoles: boolean;
  hours: number;
}

/**
 * Build the overlay for one scope (a single outlet, or a summary's outlet set).
 *
 * The split is only offered when EVERY outlet in the scope is mapped to a roster
 * and returned data — mixing rostered outlets with model-staffed ones in one
 * total would silently understate cost, so a summary that includes an unmapped
 * outlet (e.g. Shopify, which has no shop-floor roster) stays on the model.
 */
function buildRosterOverlay(scope: OutletModel[], feed: RLFeed | null): RosterOverlay | null {
  if (!feed || !scope.length) return null;
  const byKey = new Map(feed.outlets.map((o) => [`${o.category}|${o.location}`, o] as const));
  const available = scope.every(
    (o) => byKey.get(`${o.category}|${o.location}`)?.available === true
  );

  const staffByMonth = new Map<number, number>();
  const otherFixedByMonth = new Map<number, number>();
  let staffTotal = 0;
  let otherFixedTotal = 0;
  let hours = 0;
  let hasUnratedRoles = false;
  const staffMerge = new Map<string, RLStaff>();

  for (const outlet of scope) {
    // Non-staff fixed (utilities + maintenance + management) per trading day,
    // exactly as the base model strikes it, so the split reconciles to the tab.
    const otherPct = outlet.costs.utilities + outlet.costs.maintenance + outlet.costs.management;
    const otherPot = (outlet.net * otherPct) / 100;
    const perDay = outlet.tradingDays > 0 ? otherPot / outlet.tradingDays : 0;
    for (const m of outlet.months) {
      otherFixedByMonth.set(m.month, (otherFixedByMonth.get(m.month) ?? 0) + perDay * m.tradingDays);
    }
    otherFixedTotal += otherPot;

    if (!available) continue;
    const rl = byKey.get(`${outlet.category}|${outlet.location}`)!;
    hasUnratedRoles = hasUnratedRoles || rl.hasUnratedRoles;
    const oCost = rl.totals.cost; // employed-only season total for this outlet
    staffTotal += oCost;
    hours += rl.totals.hours;
    const net = outlet.net;
    const monthCount = outlet.months.length;
    for (const m of outlet.months) {
      const share = net > 0 ? m.net / net : monthCount ? 1 / monthCount : 0;
      staffByMonth.set(m.month, (staffByMonth.get(m.month) ?? 0) + oCost * share);
    }
    // merge named staff across the scope's outlets (a person can work several)
    for (const s of rl.staff) {
      const k = String(s.personId);
      const cur = staffMerge.get(k);
      if (cur) {
        cur.shifts += s.shifts;
        cur.hours += s.hours;
        cur.cost += s.cost;
        cur.roles = Array.from(new Set([...cur.roles, ...s.roles]));
      } else {
        staffMerge.set(k, { ...s, roles: [...s.roles] });
      }
    }
  }

  const staff = Array.from(staffMerge.values()).sort(
    (a, b) => b.cost - a.cost || b.hours - a.hours
  );
  return {
    available,
    mode: feed.mode ?? "actual",
    staffTotal,
    staffByMonth,
    otherFixedByMonth,
    otherFixedTotal,
    staff,
    totals: {
      staffCount: staff.length,
      shifts: staff.reduce((a, s) => a + s.shifts, 0),
      hours,
      cost: staffTotal,
    },
    hasUnratedRoles,
    hours,
  };
}

/**
 * The staffing overlay the by-hour charts draw. The rostering feed is a season
 * total per outlet, so we derive the average staff CONCURRENCY (people on shift
 * per open hour = rostered hours / trading hours) and the employed-only £/hour
 * — a rostered volunteer contributes hours but £0, so an all-volunteer roster
 * has ratePerHour 0. That average is drawn above every trading hour so the
 * rostered staffing pattern can be read straight against the sales bars.
 */
interface StaffOverlay {
  /** average people on shift per open hour, across the season */
  perHour: number;
  /** employed-only £ per rostered hour (0 for an all-volunteer roster) */
  ratePerHour: number;
  /** distinct rostered people across the season for this scope */
  headcount: number;
  /** hours > 0 but employed cost 0 — the roster is entirely volunteers */
  allVolunteers: boolean;
}

function buildStaffOverlay(
  roster: RosterOverlay | null | undefined,
  tradingHours: number
): StaffOverlay | null {
  if (!roster || !roster.available) return null;
  const hrs = roster.hours;
  if (!(hrs > 0) || !(tradingHours > 0)) return null;
  return {
    perHour: hrs / tradingHours,
    ratePerHour: roster.totals.cost / hrs, // employed-only; 0 if all volunteers
    headcount: roster.totals.staffCount,
    allVolunteers: roster.totals.cost === 0,
  };
}

// Season totals across a set of outlets — used for each summary view (all,
// retail-only, tea-rooms-only) and for the PDF cover.
function sumBuckets(outlets: OutletModel[]): Bucket {
  return outlets.reduce<Bucket>(
    (a, o) => ({
      net: a.net + o.net,
      cogs: a.cogs + o.cogs,
      fixed: a.fixed + o.fixed,
      profit: a.profit + o.profit,
      costedNet: a.costedNet + o.costedNet,
      items: a.items + o.items,
      transactions: a.transactions + o.transactions,
      dayCount: a.dayCount + o.dayCount,
    }),
    { net: 0, cogs: 0, fixed: 0, profit: 0, costedNet: 0, items: 0, transactions: 0, dayCount: 0 }
  );
}

// ---------------------------------------------------------------------------
//  Charts (inline SVG — there are up to ~30 small multiples on screen at once,
//  which a full chart library makes needlessly heavy)
// ---------------------------------------------------------------------------

const GREEN = "#1f7a4d";
const RED = "#b3261e";
const SALES = "#9db7a6";

/**
 * Breakeven bars: zero is the baseline, profit grows up in green, loss grows
 * down in red. Used for the per-month margin strip.
 */
function BreakevenBars({
  values,
  labels,
  height = 96,
  format = money,
  onBarClick,
  activeIndex,
}: {
  values: number[];
  labels: string[];
  height?: number;
  format?: (n: number) => string;
  onBarClick?: (i: number) => void;
  activeIndex?: number;
}) {
  const max = Math.max(1, ...values.map((v) => Math.abs(v)));
  const barW = 100 / Math.max(1, values.length);
  const mid = height / 2;

  return (
    <svg
      className="eo-chart"
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Profit by period; above the line is profit, below is loss"
    >
      {/* breakeven line */}
      <line x1="0" y1={mid} x2="100" y2={mid} className="eo-zero" vectorEffect="non-scaling-stroke" />
      {values.map((v, i) => {
        const h = (Math.abs(v) / max) * (mid - 4);
        const x = i * barW;
        const y = v >= 0 ? mid - h : mid;
        return (
          <g key={i}>
            <rect
              x={x + barW * 0.15}
              y={y}
              width={barW * 0.7}
              height={Math.max(h, 0.6)}
              fill={v >= 0 ? GREEN : RED}
              opacity={activeIndex == null || activeIndex === i ? 1 : 0.45}
              className={onBarClick ? "eo-bar-click" : undefined}
              onClick={onBarClick ? () => onBarClick(i) : undefined}
            >
              <title>{`${labels[i]}: ${format(v)}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Sales (pale) behind profit (green/red) for each hour slot. Both are drawn
 * from the same zero line so a slot whose profit dips below the axis is
 * unmistakable against its sales bar.
 */
function SalesProfitBars({
  slots,
  height = 110,
  compact = false,
  onSlotClick,
}: {
  slots: SlotBucket[];
  height?: number;
  compact?: boolean;
  onSlotClick?: (s: SlotBucket) => void;
}) {
  const max = Math.max(1, ...slots.map((s) => Math.max(s.net, Math.abs(s.profit))));
  const barW = 100 / Math.max(1, slots.length);
  // Leave room below the axis for losses without letting them dominate.
  const negMax = Math.max(0, ...slots.map((s) => Math.max(0, -s.profit)));
  const negFrac = Math.min(0.4, negMax / max);
  const zero = height * (1 - negFrac) - (compact ? 6 : 14);

  return (
    <svg
      className="eo-chart"
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Sales and profit by hour of day"
    >
      <line x1="0" y1={zero} x2="100" y2={zero} className="eo-zero" vectorEffect="non-scaling-stroke" />
      {slots.map((s, i) => {
        const x = i * barW;
        const sh = (s.net / max) * (zero - 2);
        const ph = (Math.abs(s.profit) / max) * (zero - 2);
        const py = s.profit >= 0 ? zero - ph : zero;
        const title = `${foldedSlotLabel(s.slot)} — sales ${money(s.net)}, profit ${money(s.profit)}, ${s.transactions} txns`;
        return (
          <g
            key={s.slot}
            className={onSlotClick ? "eo-bar-click" : undefined}
            onClick={onSlotClick ? (e) => { e.stopPropagation(); onSlotClick(s); } : undefined}
          >
            {/* full-height hit target so thin bars are still easy to click */}
            {onSlotClick && (
              <rect x={x} y="0" width={barW} height={height} fill="transparent" />
            )}
            <rect x={x + barW * 0.12} y={zero - sh} width={barW * 0.76} height={Math.max(sh, 0.5)} fill={SALES}>
              <title>{title}</title>
            </rect>
            <rect
              x={x + barW * 0.3}
              y={py}
              width={barW * 0.4}
              height={Math.max(ph, 0.5)}
              fill={s.profit >= 0 ? GREEN : RED}
            >
              <title>{title}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

function SlotAxis({ slots }: { slots: SlotBucket[] }) {
  return (
    <div className="eo-axis" style={{ gridTemplateColumns: `repeat(${slots.length}, 1fr)` }}>
      {slots.map((s) => (
        <span key={s.slot} title={foldedSlotLabel(s.slot)}>
          {s.slot < 0 ? (s.slot === -1 ? "early" : "late") : slotShortLabel(s.slot)}
        </span>
      ))}
    </div>
  );
}

/**
 * Per-hour staffing numbers, aligned column-for-column above the sales bars
 * (same 1fr grid as SlotAxis, so each number sits over its bar). The roster is
 * a season total, so this is the rostered average concurrency drawn on every
 * trading hour — the "staffing pattern across the day, mapped against sales".
 * Subtle by design: a muted number, blank on hours the outlet did not trade.
 */
function StaffPerHourRow({ slots, perHour }: { slots: SlotBucket[]; perHour: number }) {
  const n = Math.max(1, Math.round(perHour));
  return (
    <div className="eo-staffrow" style={{ gridTemplateColumns: `repeat(${slots.length}, 1fr)` }}>
      {slots.map((s) => {
        const trading = s.net > 0 || s.transactions > 0;
        return (
          <span
            key={s.slot}
            className={trading ? "on" : "off"}
            title={
              trading
                ? `${foldedSlotLabel(s.slot)}: ≈ ${perHour.toFixed(1)} rostered staff on shift (season average)`
                : `${foldedSlotLabel(s.slot)}: closed`
            }
          >
            {trading ? n : ""}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Small coloured "timetable" chip a calendar card carries: a RAG dot for how
 * the rostered (employed-only) labour sits against that period's gross margin,
 * plus the number of staff on shift. Keeps the staffing story on every tile.
 */
function StaffBadge({ rag, label, title }: { rag: StaffingRag; label: string; title: string }) {
  return (
    <div className={"eo-staffbadge rag-" + rag} title={title}>
      <span className="eo-staffdot" aria-hidden="true" />
      <span className="eo-staffbadge-text">{label}</span>
    </div>
  );
}

/**
 * One-line key that explains the staffing overlay: what the RAG colour means,
 * and what the numbers above the bars are. Sourced from the rostering feed, so
 * it names the employed-only rate (or "all volunteers — £0" when that applies).
 */
function StaffOverlayLegend({ staff }: { staff: StaffOverlay }) {
  return (
    <p className="eo-note eo-staff-legend">
      <span className="eo-staffbadge rag-ok"><span className="eo-staffdot" />healthy</span>
      <span className="eo-staffbadge rag-tight"><span className="eo-staffdot" />tight</span>
      <span className="eo-staffbadge rag-over"><span className="eo-staffdot" />labour &gt; margin</span>
      <span className="eo-staff-legend-text">
        Numbers above each bar are the rostered staff on shift that hour
        (≈{staff.perHour.toFixed(1)}/hr average, {staff.headcount} rostered{" "}
        {staff.headcount === 1 ? "person" : "people"}
        {staff.allVolunteers
          ? ", all volunteers — £0 cost"
          : `, ${moneyPence(staff.ratePerHour)}/hr employed-only`}
        ). The card dot rates that period&apos;s rostered labour against its gross margin.
      </span>
    </p>
  );
}

// ---------------------------------------------------------------------------
//  Costs panel
// ---------------------------------------------------------------------------

interface CostDraft {
  cogs: string;
  staffing: string;
  utilities: string;
  maintenance: string;
  management: string;
  staffCount: string;
  staffRate: string;
}

function draftFrom(row: {
  cogs_pct: number;
  staffing_pct: number;
  utilities_pct: number;
  maintenance_pct: number;
  management_pct?: number;
  staff_count?: number | null;
  staff_cost_per_hour?: number | null;
}): CostDraft {
  return {
    cogs: String(row.cogs_pct),
    staffing: String(row.staffing_pct),
    utilities: String(row.utilities_pct),
    maintenance: String(row.maintenance_pct),
    management: String(row.management_pct ?? 0),
    staffCount: row.staff_count == null ? "" : String(row.staff_count),
    staffRate: row.staff_cost_per_hour == null ? "" : String(row.staff_cost_per_hour),
  };
}

function CostRowEditor({
  label,
  sub,
  draft,
  onChange,
  onSave,
  onReset,
  saving,
  canReset,
}: {
  label: string;
  sub?: string;
  draft: CostDraft;
  onChange: (d: CostDraft) => void;
  onSave: () => void;
  onReset?: () => void;
  saving: boolean;
  canReset: boolean;
}) {
  const nums = {
    cogs: Number(draft.cogs) || 0,
    staffing: Number(draft.staffing) || 0,
    utilities: Number(draft.utilities) || 0,
    maintenance: Number(draft.maintenance) || 0,
    management: Number(draft.management) || 0,
  };
  // Real hourly staffing overrides the percentage entirely, so the staffing %
  // must drop out of both the total and the remainder margin — otherwise the
  // wage bill would be counted twice.
  const staffCount = Number(draft.staffCount);
  const staffRate = Number(draft.staffRate);
  const staffHourly =
    draft.staffCount !== "" && draft.staffRate !== "" &&
    Number.isFinite(staffCount) && Number.isFinite(staffRate) &&
    staffCount > 0 && staffRate > 0;

  const total =
    nums.cogs + (staffHourly ? 0 : nums.staffing) + nums.utilities + nums.maintenance + nums.management;
  const margin = 100 - total;
  const over = total > 100;

  const field = (key: keyof CostDraft, name: string, opts?: { max?: number; step?: number; dim?: boolean }) => (
    <label className={"eo-cost-field" + (opts?.dim ? " dim" : "")}>
      <span>{name}</span>
      <input
        type="number"
        min={0}
        max={opts?.max ?? 100}
        step={opts?.step ?? 0.5}
        value={draft[key]}
        onChange={(e) => onChange({ ...draft, [key]: e.target.value })}
      />
    </label>
  );

  return (
    <div className="eo-cost-row">
      <div className="eo-cost-name">
        <strong>{label}</strong>
        {sub && <span className="eo-cost-sub">{sub}</span>}
      </div>
      {field("cogs", "Cost of Goods")}
      {field("staffing", "Staffing %", { dim: staffHourly })}
      {field("staffCount", "No. of staff", { max: 999, step: 0.5 })}
      {field("staffRate", "Cost per hour £", { max: 9999, step: 0.25 })}
      {field("utilities", "Utilities")}
      {field("maintenance", "Maintenance")}
      {field("management", "Management")}
      {staffHourly ? (
        <div className="eo-cost-margin hourly">
          <span>Staffing</span>
          <strong>{`${staffCount} × £${staffRate}/hr`}</strong>
        </div>
      ) : (
        <div className={"eo-cost-margin" + (margin < 0 ? " neg" : "")}>
          <span>Profit margin</span>
          <strong>{margin.toFixed(1)}%</strong>
        </div>
      )}
      <div className="eo-cost-actions">
        <button type="button" className="eo-btn" disabled={saving || over} onClick={onSave}>
          {saving ? "Saving..." : "Save"}
        </button>
        {canReset && onReset && (
          <button type="button" className="eo-btn ghost" disabled={saving} onClick={onReset}>
            Use default
          </button>
        )}
      </div>
      {staffHourly && (
        <p className="eo-cost-note">
          Staffing is costed at {staffCount} × £{staffRate}/hr for every hour the outlet actually
          traded, so the Staffing % is ignored here. The profit margin becomes an outcome of the
          hours worked rather than a figure you set — see the outlet's own margin below. Clear
          either box to go back to the percentage.
        </p>
      )}
      {over && (
        <p className="eo-cost-warn">
          Costs total {total.toFixed(1)}% — they cannot exceed 100%, or the margin is negative
          everywhere by construction.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Main component
// ---------------------------------------------------------------------------

export function ExecOutlets() {
  const [data, setData] = useState<ApiSeason | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);

  const [cogsSource, setCogsSource] = useState<CogsSource>("modelled");
  const [staffSource, setStaffSource] = useState<StaffSource>("model");
  const [staffCosts, setStaffCosts] = useState<StaffCostRow[] | null>(null);
  const [staffAvailable, setStaffAvailable] = useState(false);
  const [staffBackfill, setStaffBackfill] = useState(false);
  const [allHours, setAllHours] = useState(false);
  const [activeOutlet, setActiveOutlet] = useState<string | null>(null);

  // Live Rostering: pull rostered staff cost/hours (employed-only) from
  // rostering-nymr and split it out of the modelled "fixed" pot on the
  // Sales-by-month table. Cached per season in rosterFeed/rosterFY.
  const [liveRostering, setLiveRostering] = useState(false);
  const [rosterFeed, setRosterFeed] = useState<RLFeed | null>(null);
  const [rosterFY, setRosterFY] = useState<number | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);

  const [costsOpen, setCostsOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);

  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);
  const [dayRows, setDayRows] = useState<Record<number, ApiDayRow[]>>({});
  const [dayLoading, setDayLoading] = useState(false);

  const [lossMonth, setLossMonth] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const [drafts, setDrafts] = useState<Record<string, CostDraft>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [items, setItems] = useState<ItemRow[] | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsCategory, setItemsCategory] = useState<"Retail" | "Tea Room">("Retail");
  const [itemsUncosted, setItemsUncosted] = useState(false);
  const [bulkMargin, setBulkMargin] = useState("50");
  const [bulkBusy, setBulkBusy] = useState(false);

  const pdfRoot = useRef<HTMLDivElement | null>(null);

  // ---- season load -------------------------------------------------------
  const load = useCallback(async (fy: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const qs = fy ? `?fiscalYear=${fy}` : "";
      const res = await fetch(`/api/dashboards/exec/outlets${qs}`, { credentials: "include" });
      if (res.status === 403) {
        setError("You do not have access to this tab (SG-Exec-Reports).");
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const json = (await res.json()) as ApiSeason;
      setData(json);
      setFiscalYear(json.fiscalYear);
      setDayRows({});
      setExpandedMonth(null);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(null);
  }, [load]);

  // Pull real staff costs (finance-nymr payroll) for the season. Purely
  // additive — if finance is unavailable the pill just stays disabled.
  useEffect(() => {
    if (!fiscalYear) return;
    let cancelled = false;
    fetch(`/api/dashboards/exec/staff-costs?fiscalYear=${fiscalYear}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j: { available: boolean; rows: StaffCostRow[] }) => {
        if (cancelled) return;
        setStaffAvailable(Boolean(j.available) && j.rows.length > 0);
        setStaffCosts(j.rows ?? []);
      })
      .catch(() => {
        if (!cancelled) { setStaffAvailable(false); setStaffCosts([]); }
      });
    return () => { cancelled = true; };
  }, [fiscalYear]);

  // If the finance link drops (or a season has no payroll yet), don't leave the
  // pill selected against no data.
  useEffect(() => {
    if (!staffAvailable && staffSource === "iplicit") setStaffSource("model");
  }, [staffAvailable, staffSource]);

  // Backfill only makes sense while the iplicit source is on.
  useEffect(() => {
    if (staffSource !== "iplicit" && staffBackfill) setStaffBackfill(false);
  }, [staffSource, staffBackfill]);

  // Pull the season's rostered labour when Live Rostering is on. Cached per
  // season; degrades to a "configured:false" feed if rostering is unreachable so
  // the tab never breaks.
  useEffect(() => {
    if (!liveRostering || !fiscalYear) return;
    if (rosterFeed && rosterFY === fiscalYear) return; // already have this season
    let cancelled = false;
    setRosterLoading(true);
    fetch(`/api/dashboards/exec/rostering-labour?fiscalYear=${fiscalYear}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j: RLFeed) => {
        if (cancelled) return;
        setRosterFeed(j);
        setRosterFY(fiscalYear);
      })
      .catch(() => {
        if (cancelled) return;
        setRosterFeed({ available: false, configured: false, outlets: [] });
        setRosterFY(fiscalYear);
      })
      .finally(() => { if (!cancelled) setRosterLoading(false); });
    return () => { cancelled = true; };
  }, [liveRostering, fiscalYear, rosterFeed, rosterFY]);

  // Only use the feed when it matches the season on screen (avoids a flash of
  // last season's staff while a new season's feed loads).
  const activeFeed = useMemo(
    () => (liveRostering && rosterFY === fiscalYear ? rosterFeed : null),
    [liveRostering, rosterFY, fiscalYear, rosterFeed]
  );

  // Seed the cost editors whenever the server's model changes.
  useEffect(() => {
    if (!data) return;
    const next: Record<string, CostDraft> = {};
    for (const row of data.cost_model) {
      next[`${row.category}|${row.location ?? ""}`] = draftFrom(row);
    }
    setDrafts(next);
  }, [data]);

  const outlets = useMemo(
    () => (data ? buildOutletModels(data, cogsSource, staffSource, staffCosts ?? [], staffBackfill) : []),
    [data, cogsSource, staffSource, staffCosts, staffBackfill]
  );

  useEffect(() => {
    if (!activeOutlet && outlets.length) {
      setActiveOutlet(`${outlets[0].category}|${outlets[0].location}`);
    }
  }, [outlets, activeOutlet]);

  const current = useMemo(
    () => outlets.find((o) => `${o.category}|${o.location}` === activeOutlet) ?? null,
    [outlets, activeOutlet]
  );

  // Best / worst / loss flags on the outlet tabs. Recomputed with the cost
  // model, so changing a percentage can move which outlet is flagged.
  const standings = useMemo(() => outletStandings(outlets), [outlets]);

  // ---- day drill-down ----------------------------------------------------
  const loadMonth = useCallback(
    async (month: number) => {
      if (dayRows[month] || !fiscalYear) return;
      setDayLoading(true);
      try {
        const res = await fetch(
          `/api/dashboards/exec/outlets/days?fiscalYear=${fiscalYear}&month=${month}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = (await res.json()) as { rows: ApiDayRow[] };
        setDayRows((prev) => ({ ...prev, [month]: json.rows }));
      } catch (e) {
        setError(String((e as Error)?.message ?? e));
      } finally {
        setDayLoading(false);
      }
    },
    [dayRows, fiscalYear]
  );

  const toggleMonth = (month: number) => {
    const next = expandedMonth === month ? null : month;
    setExpandedMonth(next);
    if (next != null) void loadMonth(next);
  };

  // Loss-making panel: load the chosen month's day data on demand.
  useEffect(() => {
    if (lossMonth != null) void loadMonth(lossMonth);
  }, [lossMonth, loadMonth]);

  // ---- cost model save ---------------------------------------------------
  const saveCosts = async (category: string, location: string) => {
    const key = `${category}|${location}`;
    const d = drafts[key];
    if (!d) return;
    setSavingKey(key);
    setSaveError(null);
    try {
      const res = await fetch("/api/dashboards/exec/costs", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          location,
          cogs_pct: Number(d.cogs) || 0,
          staffing_pct: Number(d.staffing) || 0,
          utilities_pct: Number(d.utilities) || 0,
          maintenance_pct: Number(d.maintenance) || 0,
          management_pct: Number(d.management) || 0,
          // empty string clears the hourly staffing and reverts to the %
          staff_count: d.staffCount === "" ? null : Number(d.staffCount),
          staff_cost_per_hour: d.staffRate === "" ? null : Number(d.staffRate),
        }),
      });
      const json = (await res.json()) as { cost_model?: CostModelRow[]; error?: string };
      if (!res.ok) throw new Error(json.error ?? `${res.status} ${res.statusText}`);
      if (json.cost_model && data) setData({ ...data, cost_model: json.cost_model });
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    } finally {
      setSavingKey(null);
    }
  };

  const resetOverride = async (category: string, location: string) => {
    const key = `${category}|${location}`;
    setSavingKey(key);
    setSaveError(null);
    try {
      const res = await fetch(
        `/api/dashboards/exec/costs?category=${encodeURIComponent(category)}&location=${encodeURIComponent(location)}`,
        { method: "DELETE", credentials: "include" }
      );
      const json = (await res.json()) as { cost_model?: CostModelRow[]; error?: string };
      if (!res.ok) throw new Error(json.error ?? `${res.status} ${res.statusText}`);
      if (json.cost_model && data) setData({ ...data, cost_model: json.cost_model });
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    } finally {
      setSavingKey(null);
    }
  };

  // ---- items -------------------------------------------------------------
  const loadItems = useCallback(async () => {
    if (!fiscalYear) return;
    setItemsLoading(true);
    try {
      const qs =
        `?fiscalYear=${fiscalYear}&limit=100` +
        `&category=${encodeURIComponent(itemsCategory)}` +
        (itemsUncosted ? "&uncosted=1" : "");
      const res = await fetch(`/api/dashboards/exec/items${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const json = (await res.json()) as { items: ItemRow[] };
      setItems(json.items);
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    } finally {
      setItemsLoading(false);
    }
  }, [fiscalYear, itemsCategory, itemsUncosted]);

  // Refetch on open and whenever the category / filter pill changes — the
  // uncosted filter is applied server-side so it searches the whole catalogue,
  // not just the hundred rows already on screen.
  useEffect(() => {
    if (itemsOpen) void loadItems();
  }, [itemsOpen, loadItems]);

  const saveItem = async (plu: string, itemName: string | null, mode: "cost" | "margin", value: number) => {
    try {
      const res = await fetch("/api/dashboards/exec/items", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plu, item_name: itemName, mode, value }),
      });
      if (!res.ok) {
        const j = (await res.json()) as { error?: string };
        throw new Error(j.error ?? `${res.status}`);
      }
      await loadItems();
      // an override changes cost of goods, so the outlet figures move too
      await load(fiscalYear);
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    }
  };

  /** Blanket-apply one margin across every item currently listed. */
  const applyBulkMargin = async () => {
    if (!items?.length) return;
    const value = Number(bulkMargin);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setSaveError("Enter a margin between 0 and 100.");
      return;
    }
    const overwriting = items.filter((i) => i.override_mode).length;
    const warn = overwriting
      ? `\n\n${overwriting} of these already have a hand-set cost and will be overwritten.`
      : "";
    if (
      !window.confirm(
        `Apply a ${value}% margin to all ${items.length} listed ${itemsCategory} item(s)?` +
          `\n\nThis sets each item's cost to whatever produces that margin on its own selling price.${warn}`
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setSaveError(null);
    try {
      const res = await fetch("/api/dashboards/exec/items", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "margin",
          value,
          items: items.map((i) => ({ plu: i.plu, item_name: i.item })),
        }),
      });
      const j = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(j.error ?? `${res.status}`);
      await loadItems();
      // costs moved, so the outlet figures move with them
      await load(fiscalYear);
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    } finally {
      setBulkBusy(false);
    }
  };

  const clearItem = async (plu: string) => {
    try {
      const res = await fetch(`/api/dashboards/exec/items?plu=${encodeURIComponent(plu)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(String(res.status));
      await loadItems();
      await load(fiscalYear);
    } catch (e) {
      setSaveError(String((e as Error)?.message ?? e));
    }
  };

  // ---- render ------------------------------------------------------------
  if (loading) return <div className="eo-wrap"><p className="eo-muted">Loading outlet data...</p></div>;
  if (error) return <div className="eo-wrap"><p className="eo-error">{error}</p></div>;
  if (!data) return null;

  if (!data.outlets.length) {
    return (
      <div className="eo-wrap">
        <h2 className="eo-title">Outlets by Time</h2>
        <p className="eo-empty">
          No hourly data cached yet. <code>cache.EposHourlySales</code> is filled by the on-prem
          <code> Refresh-EposSales.ps1</code> — once that has run, this tab populates automatically.
        </p>
      </div>
    );
  }

  const retail = outlets.filter((o) => o.category === "Retail");
  const tearooms = outlets.filter((o) => o.category === "Tea Room");

  const seasonTotals = sumBuckets(outlets);

  return (
    <div className="eo-wrap" ref={pdfRoot}>
      {/* ---------------- header ---------------- */}
      <div className="eo-head">
        <div>
          <h2 className="eo-title">Outlets by Time</h2>
          <p className="eo-sub">
            Sales and profit by outlet, month, day and hour. Profit uses the cost model below —
            Cost of Goods varies with takings; Staffing, Utilities and Maintenance are fixed for
            the day, so quiet hours carry their share.
          </p>
        </div>
        <div className="eo-head-controls">
          <label className="eo-select">
            <span>Season</span>
            <select
              value={fiscalYear ?? ""}
              onChange={(e) => void load(Number(e.target.value))}
            >
              {data.years.map((y) => (
                <option key={y} value={y}>{`${y - 1}/${String(y).slice(-2)}`}</option>
              ))}
            </select>
          </label>
          <div className="eo-pills" role="group" aria-label="Cost of goods source">
            <button
              type="button"
              className={"eo-pill" + (cogsSource === "modelled" ? " active" : "")}
              onClick={() => setCogsSource("modelled")}
            >
              Modelled COGS
            </button>
            <button
              type="button"
              className={"eo-pill" + (cogsSource === "actual" ? " active" : "")}
              onClick={() => setCogsSource("actual")}
              title="Real MERAC stock cost where the line has one, modelled % on the rest. Retail only."
            >
              Actual COGS
            </button>
          </div>
          <button
            type="button"
            className={"eo-pill" + (staffSource === "iplicit" ? " active" : "")}
            disabled={!staffAvailable}
            onClick={() => setStaffSource((s) => (s === "iplicit" ? "model" : "iplicit"))}
            title={
              staffAvailable
                ? "Use real payroll staff cost from finance (department totals, no personal data), by category by month, in place of the modelled staffing. Months finance hasn't run yet fall back to the model."
                : "No finance payroll data for this season yet."
            }
          >
            Iplicit staff costs
          </button>
          {staffSource === "iplicit" && (
            <button
              type="button"
              className={"eo-pill" + (staffBackfill ? " active" : "")}
              onClick={() => setStaffBackfill((v) => !v)}
              title="Only some months have a payroll run yet. Backfill carries the last full month of payroll into the earlier months (payroll is roughly constant month to month) so every month reflects real staff cost."
            >
              Back fill
            </button>
          )}
          <button
            type="button"
            className={"eo-pill" + (liveRostering ? " active" : "")}
            onClick={() => setLiveRostering((v) => !v)}
            title="Pull rostered staff cost and hours from rostering-nymr (employed-only £ — volunteers cost nothing). On the Sales-by-month table, staff cost is split out of fixed costs into its own row; click a staff figure for the named rota."
          >
            {rosterLoading ? "Live Rostering…" : "Live Rostering"}
          </button>
          <button
            type="button"
            className={"eo-pill" + (allHours ? " active" : "")}
            onClick={() => setAllHours((v) => !v)}
            title="Show every clock hour instead of folding trade outside 09:30-17:30 into edge columns"
          >
            All hours
          </button>
          <button
            type="button"
            className="eo-btn"
            onClick={() =>
              exportExecPdf({
                fiscalYearLabel: data.fiscalYearLabel,
                outlets,
                seasonTotals,
                cogsSource,
                allHours,
                lossMonth,
                dayRows,
                costAdjustments: data.cost_adjustments,
              })
            }
          >
            PDF
          </button>
        </div>
      </div>

      {data.last_refresh && (
        <p className="eo-muted eo-refresh">
          EPOS cache last refreshed {new Date(data.last_refresh).toLocaleString("en-GB")}
        </p>
      )}
      {saveError && <p className="eo-error">{saveError}</p>}

      {liveRostering && activeFeed && !activeFeed.configured && (
        <p className="eo-muted eo-refresh">
          Live Rostering: the rostering-nymr link is not configured for this environment, so staff
          cost falls back to the model.
        </p>
      )}
      {liveRostering && activeFeed && activeFeed.configured && !activeFeed.available && (
        <p className="eo-muted eo-refresh">
          Live Rostering: no rostered labour returned for this season yet — showing modelled
          staffing until a roster exists.
        </p>
      )}

      {/* ---------------- 1. costs ---------------- */}
      <section className="eo-panel">
        <button type="button" className="eo-panel-head" onClick={() => setCostsOpen((v) => !v)}>
          <span className="eo-caret">{costsOpen ? "▾" : "▸"}</span>
          <h3>Costs</h3>
          <span className="eo-panel-hint">
            percentages of sales; profit margin is the remainder
          </span>
        </button>
        {costsOpen && (
          <div className="eo-panel-body">
            {(["Retail", "Tea Room"] as const).map((category) => {
              const defKey = `${category}|`;
              const defDraft = drafts[defKey];
              const catOutlets = category === "Retail" ? retail : tearooms;
              return (
                <div key={category} className="eo-cost-group">
                  <h4>{category === "Retail" ? "Retail" : "Tea Rooms"}</h4>
                  {defDraft && (
                    <CostRowEditor
                      label="All outlets (default)"
                      draft={defDraft}
                      onChange={(d) => setDrafts((p) => ({ ...p, [defKey]: d }))}
                      onSave={() => void saveCosts(category, "")}
                      saving={savingKey === defKey}
                      canReset={false}
                    />
                  )}
                  {catOutlets.map((o) => {
                    const key = `${category}|${o.location}`;
                    const d = drafts[key] ?? draftFrom({
                      cogs_pct: o.costs.cogs,
                      staffing_pct: o.costs.staffing,
                      utilities_pct: o.costs.utilities,
                      maintenance_pct: o.costs.maintenance,
                      management_pct: o.costs.management,
                    });
                    return (
                      <CostRowEditor
                        key={key}
                        label={o.location}
                        sub={o.costs.isOverride ? "override" : "inherits default"}
                        draft={d}
                        onChange={(nd) => setDrafts((p) => ({ ...p, [key]: nd }))}
                        onSave={() => void saveCosts(category, o.location)}
                        onReset={() => void resetOverride(category, o.location)}
                        saving={savingKey === key}
                        canReset={o.costs.isOverride}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------------- 2. item costs ---------------- */}
      <section className="eo-panel">
        <button type="button" className="eo-panel-head" onClick={() => setItemsOpen((v) => !v)}>
          <span className="eo-caret">{itemsOpen ? "▾" : "▸"}</span>
          <h3>Item costs &amp; margins</h3>
          <span className="eo-panel-hint">top 100 items — set a margin % or a cash cost</span>
        </button>
        {itemsOpen && (
          <div className="eo-panel-body">
            <div className="eo-item-filters">
              <div className="eo-pills" role="group" aria-label="Category">
                {(["Retail", "Tea Room"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={"eo-pill" + (itemsCategory === c ? " active" : "")}
                    onClick={() => setItemsCategory(c)}
                  >
                    {c === "Retail" ? "Retail" : "Tea Rooms"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={"eo-pill" + (itemsUncosted ? " active" : "")}
                onClick={() => setItemsUncosted((v) => !v)}
                title="Items MERAC has no cost for — they read as a 100% margin until you set one. Items you have already overridden drop off this list."
              >
                Needs a cost
              </button>
              {itemsCategory === "Tea Room" && (
                <span className="eo-filter-note">
                  MERAC holds no cost for tea-room items, so every line here needs one setting by hand.
                </span>
              )}
            </div>

            {!!items?.length && (
              <div className="eo-bulk">
                <label>
                  <span>Apply margin to all listed</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={bulkMargin}
                    onChange={(e) => setBulkMargin(e.target.value)}
                  />
                  <em>%</em>
                </label>
                <button
                  type="button"
                  className="eo-btn sm"
                  disabled={bulkBusy}
                  onClick={() => void applyBulkMargin()}
                >
                  {bulkBusy ? "Applying..." : `Apply to all ${items.length}`}
                </button>
                <span className="eo-filter-note">
                  Sets each item's cost to whatever produces that margin on its own selling price.
                </span>
              </div>
            )}
            {itemsLoading && <p className="eo-muted">Loading items...</p>}
            {!itemsLoading && items && !items.length && (
              <p className="eo-muted">
                {itemsUncosted
                  ? "Nothing here — every item in this category has a cost."
                  : "No items found for this category."}
              </p>
            )}
            {!itemsLoading && items && !!items.length && (
              <ItemsTable items={items} onSave={saveItem} onClear={clearItem} />
            )}
          </div>
        )}
      </section>

      {/* ---------------- 3. outlet tabs ---------------- */}
      <div className="eo-outlet-tabs" role="tablist" aria-label="Outlets">
        {outlets.map((o) => {
          const key = `${o.category}|${o.location}`;
          const standing = standings.get(key) ?? null;
          const label = standingLabel(standing);
          const perMonth = o.profit / Math.max(1, o.months.length);
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeOutlet === key}
              className={"eo-outlet-tab" + (activeOutlet === key ? " active" : "")}
              data-standing={standing ?? undefined}
              title={
                label
                  ? `${o.location} — ${label}. ${money(o.profit)} profit over ${o.months.length} month(s), ` +
                    `averaging ${money(perMonth)}/month.`
                  : `${o.location} — ${money(o.profit)} profit, averaging ${money(perMonth)}/month.`
              }
              onClick={() => {
                setActiveOutlet(key);
                setExpandedMonth(null);
              }}
            >
              <span className="eo-tab-top">
                {o.location}
                {label && <span className={"eo-standing " + standing}>{label}</span>}
              </span>
              <span className="eo-tab-cat">{o.category === "Retail" ? "Retail" : "Tea Room"}</span>
            </button>
          );
        })}
        {([
          { key: "__summary", label: "All outlets", cat: "Summary" },
          { key: "__summary_retail", label: "All Retail", cat: "Summary" },
          { key: "__summary_retail_bm", label: "All Retail (No Shopify)", cat: "Summary" },
          { key: "__summary_tearoom", label: "All Tea Rooms", cat: "Summary" },
        ] as const).map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={activeOutlet === s.key}
            className={"eo-outlet-tab summary" + (activeOutlet === s.key ? " active" : "")}
            onClick={() => {
              setActiveOutlet(s.key);
              setExpandedMonth(null);
            }}
          >
            {s.label}
            <span className="eo-tab-cat">{s.cat}</span>
          </button>
        ))}
      </div>

      <p className="eo-legend">
        <span className="eo-standing best">Best</span> and{" "}
        <span className="eo-standing worst">Worst</span> rank on total profit for the season.{" "}
        <span className="eo-standing loss">Loss</span> means the outlet averages under{" "}
        {money(LOSS_THRESHOLD_PER_MONTH)} profit a month, and takes precedence — if the best
        performer is itself under that line, calling it "Best" would flatter the estate. All three
        move with the cost model above.
      </p>

      {activeOutlet?.startsWith("__summary") ? (
        (() => {
          const scope =
            activeOutlet === "__summary_retail"
              ? { list: retail, title: "All Retail", subtitle: "Retail outlets" }
              : activeOutlet === "__summary_retail_bm"
              ? {
                  list: retail.filter((o) => o.location !== "Shopify"),
                  title: "All Retail (No Shopify)",
                  subtitle: "Bricks-and-mortar retail only",
                }
              : activeOutlet === "__summary_tearoom"
              ? { list: tearooms, title: "All Tea Rooms", subtitle: "Tea rooms" }
              : { list: outlets, title: "All outlets", subtitle: "Retail + Tea Rooms" };
          const totals = sumBuckets(scope.list);
          return (
            <SummaryView
              outlets={scope.list}
              totals={totals}
              fiscalYear={data.fiscalYear}
              allHours={allHours}
              cogsSource={cogsSource}
              title={scope.title}
              subtitle={scope.subtitle}
              onOpenModal={setModal}
              roster={buildRosterOverlay(scope.list, activeFeed)}
            />
          );
        })()
      ) : current ? (
        <OutletView
          outlet={current}
          fiscalYear={data.fiscalYear}
          allHours={allHours}
          cogsSource={cogsSource}
          onOpenModal={setModal}
          roster={buildRosterOverlay([current], activeFeed)}
          expandedMonth={expandedMonth}
          onToggleMonth={toggleMonth}
          dayRows={expandedMonth != null ? dayRows[expandedMonth] : undefined}
          dayLoading={dayLoading}
          costAdjust={
            expandedMonth != null
              ? data.cost_adjustments.find(
                  (a) => a.location === current.location && a.month === expandedMonth
                )?.cost_adjust ?? 0
              : 0
          }
        />
      ) : null}

      {/* ---------------- 5. loss-making days & hours ---------------- */}
      <LossesView
        outlets={outlets}
        fiscalYear={data.fiscalYear}
        month={lossMonth}
        onMonth={setLossMonth}
        dayRows={lossMonth != null ? dayRows[lossMonth] : undefined}
        loading={dayLoading}
        cogsSource={cogsSource}
        allHours={allHours}
        costAdjustments={data.cost_adjustments}
      />

      {/* ---------------- 6. optimiser (below the losses) ---------------- */}
      <OptimiserPanel
        outlet={activeOutlet === "__summary" || activeOutlet?.startsWith("__summary") ? null : current}
        fiscalYear={data.fiscalYear}
        cogsSource={cogsSource}
        costAdjustments={data.cost_adjustments}
      />

      <ExecModal
        modal={modal}
        fiscalYear={data.fiscalYear}
        cogsSource={cogsSource}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Items table
// ---------------------------------------------------------------------------

function ItemsTable({
  items,
  onSave,
  onClear,
}: {
  items: ItemRow[];
  onSave: (plu: string, name: string | null, mode: "cost" | "margin", value: number) => Promise<void>;
  onClear: (plu: string) => Promise<void>;
}) {
  const [edit, setEdit] = useState<Record<string, { mode: "cost" | "margin"; value: string }>>({});

  return (
    <div className="eo-scroll">
      <table className="eo-table eo-items">
        <thead>
          <tr>
            <th>Item</th>
            <th className="num">Qty</th>
            <th className="num">Sales (net)</th>
            <th className="num">Cost</th>
            <th className="num">Margin</th>
            <th className="num">Margin %</th>
            <th className="num">Cost cover</th>
            <th>Override</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const e = edit[it.plu] ?? {
              mode: it.override_mode ?? "margin",
              value:
                it.override_value != null
                  ? String(it.override_value)
                  : it.margin_pct != null
                  ? it.margin_pct.toFixed(1)
                  : "",
            };
            const set = (patch: Partial<typeof e>) =>
              setEdit((p) => ({ ...p, [it.plu]: { ...e, ...patch } }));
            return (
              <tr key={it.plu} className={it.override_mode ? "overridden" : undefined}>
                <td>
                  {it.item ?? it.plu}
                  {it.analysis && <span className="eo-item-an">{it.analysis}</span>}
                </td>
                <td className="num">{it.qty.toLocaleString("en-GB")}</td>
                <td className="num">{money(it.net)}</td>
                <td className="num">{moneyPence(it.effective_cost)}</td>
                <td className={"num" + (it.margin < 0 ? " neg" : "")}>{money(it.margin)}</td>
                <td className={"num" + ((it.margin_pct ?? 0) < 0 ? " neg" : "")}>
                  {pct(it.margin_pct)}
                </td>
                <td className="num">
                  {it.coverage_pct == null ? "--" : (
                    <span className={it.coverage_pct < 60 ? "eo-cover-low" : undefined}>
                      {it.coverage_pct.toFixed(0)}%
                    </span>
                  )}
                </td>
                <td className="eo-item-edit">
                  <select value={e.mode} onChange={(ev) => set({ mode: ev.target.value as "cost" | "margin" })}>
                    <option value="margin">Margin %</option>
                    <option value="cost">Unit cost</option>
                  </select>
                  <input
                    type="number"
                    step={e.mode === "margin" ? 0.1 : 0.01}
                    min={0}
                    max={e.mode === "margin" ? 100 : undefined}
                    value={e.value}
                    onChange={(ev) => set({ value: ev.target.value })}
                  />
                </td>
                <td className="eo-item-actions">
                  <button
                    type="button"
                    className="eo-btn sm"
                    disabled={e.value === ""}
                    onClick={() => void onSave(it.plu, it.item, e.mode, Number(e.value))}
                  >
                    Set
                  </button>
                  {it.override_mode && (
                    <button type="button" className="eo-btn sm ghost" onClick={() => void onClear(it.plu)}>
                      Clear
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Opening hours & staffing what-if
// ---------------------------------------------------------------------------

function Delta({ value, invert = false }: { value: number; invert?: boolean }) {
  if (Math.abs(value) < 0.5) return <span className="eo-delta flat">no change</span>;
  const good = invert ? value < 0 : value > 0;
  return (
    <span className={"eo-delta " + (good ? "up" : "down")}>
      {value > 0 ? "+" : "−"}
      {money(Math.abs(value)).replace("-", "")}
    </span>
  );
}

function ModellingPanel({ outlet }: { outlet: OutletModel }) {
  // Only hours the outlet actually traded can be opened or closed.
  const slots = useMemo(
    () => outlet.slots.filter((s) => s.dayCount > 0).sort((a, b) => a.slot - b.slot),
    [outlet]
  );

  const baseStaff = defaultStaffCount(outlet);
  // Collapsed by default — the panel is tall, and most visits to an outlet are
  // to read its figures, not to model.
  const [open, setOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const [closeIdx, setCloseIdx] = useState(Math.max(0, slots.length - 1));
  const [staffBySlot, setStaffBySlot] = useState<Record<number, number>>({});
  const [rate, setRate] = useState<number>(() => Math.round(modelRate(outlet) * 100) / 100);
  const [foldIn, setFoldIn] = useState(false);

  // Switching outlet must reset the scenario — otherwise you are looking at
  // Pickering's rota applied to Levisham's hours.
  const key = `${outlet.category}|${outlet.location}`;
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current !== key) {
      lastKey.current = key;
      setOpenIdx(0);
      setCloseIdx(Math.max(0, slots.length - 1));
      setStaffBySlot({});
      setRate(Math.round(modelRate(outlet) * 100) / 100);
    }
  }, [key, slots.length, outlet]);

  const result = useMemo(() => {
    if (!slots.length) return null;
    return runScenario(outlet, {
      openSlot: slots[Math.min(openIdx, slots.length - 1)].slot,
      closeSlot: slots[Math.min(closeIdx, slots.length - 1)].slot,
      staffBySlot,
      ratePerHour: rate,
      foldInOutOfHours: foldIn,
    });
  }, [outlet, slots, openIdx, closeIdx, staffBySlot, rate, foldIn]);

  if (!slots.length || !result) return null;

  const openSlot = slots[Math.min(openIdx, slots.length - 1)].slot;
  const closeSlot = slots[Math.min(closeIdx, slots.length - 1)].slot;
  const changed =
    openIdx !== 0 ||
    closeIdx !== slots.length - 1 ||
    Object.keys(staffBySlot).length > 0 ||
    Math.abs(rate - modelRate(outlet)) > 0.005;

  const reset = () => {
    setOpenIdx(0);
    setCloseIdx(slots.length - 1);
    setStaffBySlot({});
    setRate(Math.round(modelRate(outlet) * 100) / 100);
  };

  const d = {
    net: result.net - result.baseline.net,
    cogs: result.cogs - result.baseline.cogs,
    staffing: result.staffing - result.baseline.staffing,
    profit: result.profit - result.baseline.profit,
  };

  return (
    <section className="eo-panel eo-model-panel">
      <button
        type="button"
        className="eo-panel-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="eo-caret">{open ? "▾" : "▸"}</span>
        <h3>Opening hours &amp; staffing model</h3>
        <span className="eo-panel-hint">
          {changed ? "modelled — not the actual hours" : "drag the hours, tune the rota, watch the profit"}
        </span>
        {changed && <span className="eo-model-flag">modelling on</span>}
      </button>

      {!open ? null : (
      <div className="eo-panel-body eo-model-body">
      {changed && (
        <div className="eo-model-resetbar">
          <button type="button" className="eo-btn ghost sm" onClick={reset}>
            Reset to actual
          </button>
        </div>
      )}

      <div className="eo-model-grid">
        {/* ---- hours ---- */}
        <div className="eo-model-hours">
          <div className="eo-model-times">
            <div>
              <span>Opens</span>
              <strong>{slotShortLabel(openSlot)}</strong>
            </div>
            <div>
              <span>Closes</span>
              <strong>{slotShortLabel(closeSlot + 60)}</strong>
            </div>
            <div>
              <span>Hours open / day</span>
              <strong>{closeIdx - openIdx + 1}</strong>
            </div>
          </div>

          <label className="eo-slider">
            <span>Opening time</span>
            <input
              type="range"
              min={0}
              max={slots.length - 1}
              step={1}
              value={openIdx}
              onChange={(e) => {
                const v = Number(e.target.value);
                setOpenIdx(v);
                if (v > closeIdx) setCloseIdx(v);
              }}
            />
          </label>

          <label className="eo-slider">
            <span>Closing time</span>
            <input
              type="range"
              min={0}
              max={slots.length - 1}
              step={1}
              value={closeIdx}
              onChange={(e) => {
                const v = Number(e.target.value);
                setCloseIdx(v);
                if (v < openIdx) setOpenIdx(v);
              }}
            />
          </label>

          <label className="eo-slider rate">
            <span>Staff cost per hour £</span>
            <input
              type="number"
              min={0}
              step={0.25}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value) || 0)}
            />
            {!outlet.costs.staffHourly && (
              <em>implied from the {outlet.costs.staffing}% staffing figure</em>
            )}
          </label>

          <label className="eo-foldin">
            <input type="checkbox" checked={foldIn} onChange={(e) => setFoldIn(e.target.checked)} />
            <span>
              Fold in out-of-hours sales
              <em>keep the closed hours' takings (customers buy in the open hours); only staffing falls</em>
            </span>
          </label>
        </div>

        {/* ---- rota ---- */}
        <div className="eo-model-rota">
          <div className="eo-rota-head">
            <strong>Staff on shift, by hour</strong>
            <span>default {baseStaff}</span>
          </div>
          <div className="eo-rota-grid">
            {slots.map((s, i) => {
              const shut = i < openIdx || i > closeIdx;
              const val = staffBySlot[s.slot] ?? baseStaff;
              return (
                <label key={s.slot} className={"eo-rota-cell" + (shut ? " shut" : "")}>
                  <span>{slotShortLabel(s.slot)}</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={shut ? "" : val}
                    disabled={shut}
                    placeholder={shut ? "—" : undefined}
                    onChange={(e) =>
                      setStaffBySlot((p) => ({ ...p, [s.slot]: Number(e.target.value) || 0 }))
                    }
                  />
                  <em>{money(s.net)}</em>
                </label>
              );
            })}
          </div>
          <p className="eo-note">
            Each box is the headcount for that hour across the season. The figure underneath is
            what that hour actually took.
          </p>
        </div>
      </div>

      {/* ---- outcome ---- */}
      <div className="eo-model-out">
        <table className="eo-table eo-model-table">
          <thead>
            <tr>
              <th>&nbsp;</th>
              <th className="num">Actual</th>
              <th className="num">Modelled</th>
              <th className="num">Change</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Sales (net)</th>
              <td className="num">{money(result.baseline.net)}</td>
              <td className="num">{money(result.net)}</td>
              <td className="num"><Delta value={d.net} /></td>
            </tr>
            {foldIn && (
              <tr className="eo-row-sub">
                <th scope="row">— of which folded in</th>
                <td className="num">—</td>
                <td className="num">{money(result.foldInSales)}</td>
                <td className="num">out-of-hours takings kept</td>
              </tr>
            )}
            <tr>
              <th scope="row">Cost of goods</th>
              <td className="num">{money(result.baseline.cogs)}</td>
              <td className="num">{money(result.cogs)}</td>
              <td className="num"><Delta value={d.cogs} invert /></td>
            </tr>
            <tr>
              <th scope="row">Staffing</th>
              <td className="num">{money(result.baseline.staffing)}</td>
              <td className="num">{money(result.staffing)}</td>
              <td className="num"><Delta value={d.staffing} invert /></td>
            </tr>
            <tr className="eo-row-sub">
              <th scope="row">Utilities &amp; maintenance</th>
              <td className="num">{money(result.baseline.otherFixed)}</td>
              <td className="num">{money(result.otherFixed)}</td>
              <td className="num"><span className="eo-delta flat">fixed</span></td>
            </tr>
            <tr className="eo-model-profit">
              <th scope="row">Profit</th>
              <td className={"num" + (result.baseline.profit < 0 ? " neg" : "")}>
                {money(result.baseline.profit)}
              </td>
              <td className={"num" + (result.profit < 0 ? " neg" : "")}>{money(result.profit)}</td>
              <td className="num"><Delta value={d.profit} /></td>
            </tr>
            <tr className="eo-row-sub">
              <th scope="row">Margin</th>
              <td className="num">{pct(result.baseline.marginPct)}</td>
              <td className="num">{pct(result.marginPct)}</td>
              <td className="num">
                {result.marginPct != null && result.baseline.marginPct != null
                  ? `${result.marginPct - result.baseline.marginPct >= 0 ? "+" : "−"}${Math.abs(
                      result.marginPct - result.baseline.marginPct
                    ).toFixed(1)} pts`
                  : "--"}
              </td>
            </tr>
            <tr className="eo-row-sub">
              <th scope="row">Hours open (season)</th>
              <td className="num">{result.baseline.hours.toLocaleString("en-GB")}</td>
              <td className="num">{result.hours.toLocaleString("en-GB")}</td>
              <td className="num">
                {result.hours - result.baseline.hours === 0
                  ? "—"
                  : (result.hours - result.baseline.hours).toLocaleString("en-GB")}
              </td>
            </tr>
          </tbody>
        </table>

        <p className="eo-model-caveat">
          <strong>Read this before acting on it.</strong> Closing an hour is modelled as losing
          <em> all</em> of that hour's takings. In practice some of those customers buy earlier or
          later instead, so the revenue loss shown is the worst case and the real outcome is
          somewhat better. Utilities and maintenance are held fixed — they do not fall when you
          shorten the day. A result close to the line is "worth investigating", not proven.
        </p>
      </div>
      </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Click-through popups
// ---------------------------------------------------------------------------

export type ModalState =
  | { kind: "items"; outlet: OutletModel; month: number | null; focus: "sales" | "cogs" }
  | { kind: "fixed"; outlet: OutletModel; month: number | null; monthModel: MonthModel | null }
  | { kind: "slot"; titleName: string; slot: SlotBucket; label: string; locations: string[]; date?: string; month?: number; fiscalYear: number }
  | { kind: "staff"; titleName: string; period: string; mode: "actual" | "forecast"; staff: RLStaff[]; totals: RLTotals; hasUnratedRoles: boolean }
  | null;

function Modal({ title, sub, onClose, children }: {
  title: React.ReactNode;
  sub?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="eo-modal-backdrop" onClick={onClose}>
      <div className="eo-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="eo-modal-head">
          <div>
            <h3>{title}</h3>
            {sub && <p>{sub}</p>}
          </div>
          <button type="button" className="eo-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="eo-modal-body">{children}</div>
      </div>
    </div>
  );
}

/** Effective per-item cost, matching the outlet model's rules exactly. */
function itemEffectiveCost(
  it: BreakdownItem,
  cogsPct: number,
  source: CogsSource,
  hasActual: boolean
): number {
  if (it.override_mode === "cost" && it.override_value != null) return it.qty * it.override_value;
  if (it.override_mode === "margin" && it.override_value != null) return it.net * (1 - it.override_value / 100);
  if (source === "modelled" || !hasActual) return (it.net * cogsPct) / 100;
  const uncosted = Math.max(0, it.net - it.costed_net);
  return it.actual_cost + (uncosted * cogsPct) / 100;
}

interface BreakdownItem {
  plu: string;
  item: string | null;
  analysis: string | null;
  qty: number;
  net: number;
  actual_cost: number;
  costed_net: number;
  override_mode: "cost" | "margin" | null;
  override_value: number | null;
}

/** Line-item detail behind a sales row or the cost-of-goods row. */
function ItemBreakdownModal({
  outlet, month, focus, fiscalYear, cogsSource, onClose,
}: {
  outlet: OutletModel;
  month: number | null;
  focus: "sales" | "cogs";
  fiscalYear: number;
  cogsSource: CogsSource;
  onClose: () => void;
}) {
  const [items, setItems] = useState<BreakdownItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // The authoritative row total comes from the outlet model (the hourly cache),
  // so the popup reconciles to the figure the row shows.
  const rowNet = month == null ? outlet.net : (outlet.months.find((m) => m.month === month)?.net ?? 0);
  const hasActual = categoryHasActualCost(outlet.category);

  useEffect(() => {
    let cancelled = false;
    const qs =
      `?category=${encodeURIComponent(outlet.category)}` +
      `&location=${encodeURIComponent(outlet.location)}` +
      `&fiscalYear=${fiscalYear}` +
      (month == null ? "" : `&month=${month}`);
    fetch(`/api/dashboards/exec/items/breakdown${qs}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j: { items: BreakdownItem[] }) => { if (!cancelled) setItems(j.items); })
      .catch((e) => { if (!cancelled) setErr(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [outlet.category, outlet.location, fiscalYear, month]);

  const period = month == null ? `season ${fiscalYear - 1}/${String(fiscalYear).slice(-2)}`
    : fiscalMonthLabel(month, fiscalYear);

  const rows = (items ?? []).map((it) => {
    const cost = itemEffectiveCost(it, outlet.costs.cogs, cogsSource, hasActual);
    return { ...it, cost, margin: it.net - cost };
  });
  const itemsNet = rows.reduce((a, r) => a + r.net, 0);
  const itemsCost = rows.reduce((a, r) => a + r.cost, 0);
  // Lines not in the product cache (some retail is non-Stock) — shown so the
  // popup total ties to the row. Costed at the modelled % so it is not free.
  const residualNet = Math.round((rowNet - itemsNet) * 100) / 100;
  const residualCost = residualNet > 0 ? (residualNet * outlet.costs.cogs) / 100 : 0;
  const totalNet = itemsNet + Math.max(0, residualNet);
  const totalCost = itemsCost + residualCost;

  return (
    <Modal
      title={`${outlet.location} — ${focus === "cogs" ? "cost of goods by item" : "sales by item"}`}
      sub={`${period} · cost of goods: ${cogsSource === "actual" ? "actual (blended)" : "modelled %"}`}
      onClose={onClose}
    >
      {err && <p className="eo-error">{err}</p>}
      {!items && !err && <p className="eo-muted">Loading line items…</p>}
      {items && (
        <div className="eo-scroll cap">
          <table className="eo-table eo-items">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Qty</th>
                <th className="num">Sales (net)</th>
                <th className="num">Cost</th>
                <th className="num">Profit</th>
                <th className="num">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.plu}>
                  <td>
                    {r.item ?? r.plu}
                    {r.analysis && <span className="eo-item-an">{r.analysis}</span>}
                    {r.override_mode && <span className="eo-item-an">hand-set {r.override_mode}</span>}
                  </td>
                  <td className="num">{r.qty.toLocaleString("en-GB")}</td>
                  <td className="num">{money(r.net)}</td>
                  <td className="num">{money(r.cost)}</td>
                  <td className={"num" + (r.margin < 0 ? " neg" : "")}>{money(r.margin)}</td>
                  <td className={"num" + (r.net > 0 && r.margin / r.net < 0 ? " neg" : "")}>
                    {r.net > 0 ? `${((r.margin / r.net) * 100).toFixed(1)}%` : "--"}
                  </td>
                </tr>
              ))}
              {residualNet > 0.5 && (
                <tr className="eo-row-sub">
                  <td>Other lines <span className="eo-item-an">not in the product cache; costed at the modelled {outlet.costs.cogs}%</span></td>
                  <td className="num">—</td>
                  <td className="num">{money(residualNet)}</td>
                  <td className="num">{money(residualCost)}</td>
                  <td className={"num" + (residualNet - residualCost < 0 ? " neg" : "")}>{money(residualNet - residualCost)}</td>
                  <td className="num">—</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="eo-model-profit">
                <th scope="row">Total</th>
                <td className="num">—</td>
                <td className="num">{money(totalNet)}</td>
                <td className="num">{money(totalCost)}</td>
                <td className={"num" + (totalNet - totalCost < 0 ? " neg" : "")}>{money(totalNet - totalCost)}</td>
                <td className="num">{totalNet > 0 ? `${(((totalNet - totalCost) / totalNet) * 100).toFixed(1)}%` : "--"}</td>
              </tr>
            </tfoot>
          </table>
          <p className="eo-note">
            Profit here is sales less cost of goods only — it does not carry the outlet's fixed
            costs (staffing, utilities, maintenance, management), which are not attributable to a
            single line. The outlet's bottom-line profit is on the tab.
          </p>
        </div>
      )}
    </Modal>
  );
}

/** How an outlet's fixed costs are made up. */
function FixedCostsModal({
  outlet, month, monthModel, fiscalYear, onClose,
}: {
  outlet: OutletModel;
  month: number | null;
  monthModel: MonthModel | null;
  fiscalYear: number;
  onClose: () => void;
}) {
  const c = outlet.costs;
  const net = month == null ? outlet.net : (monthModel?.net ?? 0);
  const fixedTotal = month == null ? outlet.fixed : (monthModel?.fixed ?? 0);
  const tradingDays = month == null ? outlet.tradingDays : (monthModel?.tradingDays ?? 0);
  const hours = month == null ? outlet.tradingHours : (monthModel?.dayCount ?? 0);

  // Each fixed component's share of the fixed pot (proportional to its %).
  const staffingInPot = !c.staffHourly;
  const parts: { name: string; pct: number | null; amount: number }[] = [];
  if (staffingInPot) parts.push({ name: "Staffing", pct: c.staffing, amount: (net * c.staffing) / 100 });
  parts.push({ name: "Utilities", pct: c.utilities, amount: (net * c.utilities) / 100 });
  parts.push({ name: "Maintenance", pct: c.maintenance, amount: (net * c.maintenance) / 100 });
  parts.push({ name: "Management", pct: c.management, amount: (net * c.management) / 100 });
  if (c.staffHourly) {
    parts.push({
      name: "Staffing (hourly)",
      pct: null,
      amount: outlet.staffPerHour * hours,
    });
  }

  const period = month == null ? `season ${fiscalYear - 1}/${String(fiscalYear).slice(-2)}`
    : fiscalMonthLabel(month, fiscalYear);

  return (
    <Modal title={`${outlet.location} — fixed costs`} sub={period} onClose={onClose}>
      <div className="eo-scroll">
        <table className="eo-table">
          <thead>
            <tr><th>Component</th><th className="num">Rate</th><th className="num">Amount</th></tr>
          </thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td className="num">{p.pct == null ? `${money(outlet.staffPerHour)}/hr` : `${p.pct}% of net`}</td>
                <td className="num">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="eo-model-profit">
              <th scope="row">Total fixed costs</th>
              <td className="num">—</td>
              <td className="num">{money(fixedTotal)}</td>
            </tr>
          </tfoot>
        </table>
        <p className="eo-note">
          Fixed costs are struck at season level ({money(outlet.fixed)} over {outlet.tradingDays}{" "}
          trading days = <strong>{moneyPence(outlet.fixedPerDay)}/day</strong>
          {c.staffHourly ? ", plus hourly staffing" : ""}), then spread across each day's trading
          hours — so a quiet hour still carries its share. That is why a slow period can show a
          loss even when the outlet is fine for the year.
          {month != null && ` This month carried ${tradingDays} of those trading days.`}
        </p>
      </div>
    </Modal>
  );
}

/** One clicked hour bar: transactions, sales and profit for that hour. */
interface TxnItem { date: string | null; time: string; trans_no: string; location: string; net: number; items: number }

function SlotModal({ titleName, slot, label, locations, date, month, fiscalYear, onClose }: {
  titleName: string;
  slot: SlotBucket;
  label: string;
  locations: string[];
  date?: string;
  month?: number;
  fiscalYear: number;
  onClose: () => void;
}) {
  const m = slot.net > 0 ? (slot.profit / slot.net) * 100 : null;
  const [showTxns, setShowTxns] = useState(false);
  const [txns, setTxns] = useState<{ rows: TxnItem[]; total: number; totalNet: number; capped: boolean; available: boolean; multiOutlet: boolean } | null>(null);
  const [txnErr, setTxnErr] = useState<string | null>(null);

  useEffect(() => {
    if (!showTxns || txns || slot.slot < 0) return;
    let cancelled = false;
    const qs =
      `?locations=${encodeURIComponent(locations.join("|"))}&slot=${slot.slot}&fiscalYear=${fiscalYear}` +
      (date ? `&date=${date}` : month != null ? `&month=${month}` : "");
    fetch(`/api/dashboards/exec/transactions${qs}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j) => { if (!cancelled) setTxns(j); })
      .catch((e) => { if (!cancelled) setTxnErr(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [showTxns, txns, locations, slot.slot, fiscalYear, date, month]);

  // Edge buckets (before 09:30 / after 17:30) fold several hours together, so a
  // single HourSlot lookup wouldn't match — only offer the drill on real hours.
  const canDrill = slot.slot >= 0;

  return (
    <Modal title={`${titleName} — ${foldedSlotLabel(slot.slot)}`} sub={label} onClose={onClose}>
      <table className="eo-table eo-model-table">
        <tbody>
          <tr>
            <th scope="row">Transactions</th>
            <td className="num">
              {canDrill ? (
                <button type="button" className="eo-drill-btn" onClick={() => setShowTxns((v) => !v)}>
                  {slot.transactions.toLocaleString("en-GB")}
                </button>
              ) : (
                slot.transactions.toLocaleString("en-GB")
              )}
            </td>
          </tr>
          <tr><th scope="row">Items sold</th><td className="num">{slot.items.toLocaleString("en-GB")}</td></tr>
          <tr><th scope="row">Sales (net)</th><td className="num">{money(slot.net)}</td></tr>
          <tr><th scope="row">Cost of goods</th><td className="num">{money(slot.cogs)}</td></tr>
          <tr><th scope="row">Fixed costs</th><td className="num">{money(slot.fixed)}</td></tr>
          <tr className="eo-model-profit">
            <th scope="row">Profit</th>
            <td className={"num" + (slot.profit < 0 ? " neg" : "")}>{money(slot.profit)}</td>
          </tr>
          <tr className="eo-row-sub"><th scope="row">Margin</th><td className="num">{pct(m)}</td></tr>
          <tr className="eo-row-sub">
            <th scope="row">Avg transaction</th>
            <td className="num">{slot.transactions > 0 ? money(slot.net / slot.transactions) : "--"}</td>
          </tr>
        </tbody>
      </table>

      {canDrill && !showTxns && (
        <p className="eo-note">Click the transaction count to list the individual sales and the time each was rung.</p>
      )}

      {showTxns && (
        <div className="eo-txns">
          {txnErr && <p className="eo-error">{txnErr}</p>}
          {!txns && !txnErr && <p className="eo-muted">Loading transactions…</p>}
          {txns && !txns.available && (
            <p className="eo-muted">Transaction detail is held for the last two seasons only.</p>
          )}
          {txns && txns.available && (
            <>
              <h4 className="eo-h4">
                Sales this hour <span className="eo-h4-sub">{txns.total.toLocaleString("en-GB")} transactions · {money(txns.totalNet)}</span>
              </h4>
              <div className="eo-scroll cap">
                <table className="eo-table">
                  <thead>
                    <tr>
                      {!date && <th>Date</th>}
                      {txns.multiOutlet && <th>Outlet</th>}
                      <th>Time</th><th>Transaction</th>
                      <th className="num">Items</th><th className="num">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.rows.map((t, i) => (
                      <tr key={t.trans_no + i}>
                        {!date && (
                          <td>{t.date ? new Date(`${t.date}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }) : "--"}</td>
                        )}
                        {txns.multiOutlet && <td>{t.location}</td>}
                        <td>{t.time}</td>
                        <td><code className="eo-txn-no">{t.trans_no}</code></td>
                        <td className="num">{t.items}</td>
                        <td className="num">{money(t.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {txns.capped && (
                  <p className="eo-note">Showing the first 1,000 by time — narrow to a single day (open a month, then a day) for the full list.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <p className="eo-note">
        Cost of goods and fixed costs follow the cost model on the tab. Transaction times come
        straight from the till (last two seasons held).
      </p>
    </Modal>
  );
}

/** The named rota behind a rostered staff-cost figure. Employed-only £. */
function StaffModal({
  titleName, period, mode, staff, totals, hasUnratedRoles, onClose,
}: {
  titleName: string;
  period: string;
  mode: "actual" | "forecast";
  staff: RLStaff[];
  totals: RLTotals;
  hasUnratedRoles: boolean;
  onClose: () => void;
}) {
  const anyVolunteer = staff.some((s) => s.hours > 0 && s.cost === 0);
  return (
    <Modal
      title={
        <>
          {titleName} — rostered staff{" "}
          <span className={"eo-chip " + (mode === "forecast" ? "alt" : "")}>
            {mode === "forecast" ? "forecast" : "actual"}
          </span>
        </>
      }
      sub={period}
      onClose={onClose}
    >
      <div className="eo-scroll">
        <table className="eo-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Role(s)</th>
              <th className="num">Shifts</th>
              <th className="num">Hours</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr><td colSpan={5} className="eo-muted">No staff rostered for this period.</td></tr>
            )}
            {staff.map((s) => (
              <tr key={String(s.personId)}>
                <td>{s.name}</td>
                <td>{s.roles.join(", ") || "—"}</td>
                <td className="num">{s.shifts.toLocaleString("en-GB")}</td>
                <td className="num">{s.hours.toLocaleString("en-GB", { maximumFractionDigits: 1 })}</td>
                <td className="num">{s.cost > 0 ? money(s.cost) : <span className="eo-muted">£0 (volunteer)</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="eo-model-profit">
              <th scope="row">Total ({totals.staffCount} staff)</th>
              <td className="num">—</td>
              <td className="num">{totals.shifts.toLocaleString("en-GB")}</td>
              <td className="num">{totals.hours.toLocaleString("en-GB", { maximumFractionDigits: 1 })}</td>
              <td className="num">{money(totals.cost)}</td>
            </tr>
          </tfoot>
        </table>
        <p className="eo-note">
          Rostered hours and cost come live from rostering-nymr. Cost is{" "}
          <strong>employed-only</strong>: a rostered volunteer shows their hours but £0
          {anyVolunteer ? " (as above)" : ""}. {mode === "forecast"
            ? "This season is in the future, so these are the planned rota (forecast)."
            : "These are the hours actually rostered for the season (actual)."}
          {hasUnratedRoles && " Some roles have no cost rate set yet, so their hours are shown but not costed."}
        </p>
      </div>
    </Modal>
  );
}

function ExecModal({ modal, fiscalYear, cogsSource, onClose }: {
  modal: ModalState;
  fiscalYear: number;
  cogsSource: CogsSource;
  onClose: () => void;
}) {
  if (!modal) return null;
  if (modal.kind === "items")
    return <ItemBreakdownModal outlet={modal.outlet} month={modal.month} focus={modal.focus}
      fiscalYear={fiscalYear} cogsSource={cogsSource} onClose={onClose} />;
  if (modal.kind === "fixed")
    return <FixedCostsModal outlet={modal.outlet} month={modal.month} monthModel={modal.monthModel}
      fiscalYear={fiscalYear} onClose={onClose} />;
  if (modal.kind === "staff")
    return <StaffModal titleName={modal.titleName} period={modal.period} mode={modal.mode}
      staff={modal.staff} totals={modal.totals} hasUnratedRoles={modal.hasUnratedRoles} onClose={onClose} />;
  return <SlotModal titleName={modal.titleName} slot={modal.slot} label={modal.label}
    locations={modal.locations} date={modal.date} month={modal.month}
    fiscalYear={modal.fiscalYear} onClose={onClose} />;
}

// ---------------------------------------------------------------------------
//  Per-outlet view
// ---------------------------------------------------------------------------

function MonthTable({ months, fiscalYear, outlet, onOpenModal, roster, onOpenStaff }: {
  months: MonthModel[];
  fiscalYear: number;
  outlet?: OutletModel;
  onOpenModal?: (m: ModalState) => void;
  roster?: RosterOverlay | null;
  onOpenStaff?: (() => void) | null;
}) {
  // Live Rostering splits staff out of the modelled fixed pot: staff = the
  // rostered employed-only £ (apportioned per month by net), fixed = utilities +
  // maintenance (+ management) only. Profit and margin are recomputed from the
  // split so the table reconciles.
  const live = !!(roster && roster.available);
  const staffOf = (m: MonthModel) => roster?.staffByMonth.get(m.month) ?? 0;
  const otherOf = (m: MonthModel) => roster?.otherFixedByMonth.get(m.month) ?? 0;
  const costsOf = (m: MonthModel) => (live ? m.cogs + otherOf(m) + staffOf(m) : m.cogs + m.fixed);
  const profitOf = (m: MonthModel) => (live ? m.net - m.cogs - otherOf(m) - staffOf(m) : m.profit);

  const total = months.reduce(
    (a, m) => ({
      net: a.net + m.net,
      profit: a.profit + profitOf(m),
      days: a.days + m.tradingDays,
      costs: a.costs + costsOf(m),
      cogs: a.cogs + m.cogs,
      staff: a.staff + staffOf(m),
      other: a.other + otherOf(m),
    }),
    { net: 0, profit: 0, days: 0, costs: 0, cogs: 0, staff: 0, other: 0 }
  );

  // A drillable figure: a plain <td> in the summary, a clickable one in an
  // outlet view (where we can open its line-item / fixed-cost detail).
  const drill = (
    key: string | number,
    content: React.ReactNode,
    open: (() => void) | null,
    cls = ""
  ) =>
    open && outlet ? (
      <td key={key} className={"num eo-drill " + cls}>
        <button type="button" className="eo-drill-btn" onClick={open}>{content}</button>
      </td>
    ) : (
      <td key={key} className={"num " + cls}>{content}</td>
    );

  // Staff cell is clickable in BOTH the outlet and summary tables (the popup
  // lists the named rota for the whole period regardless of scope).
  const staffCell = (key: string | number, value: number, cls = "") =>
    onOpenStaff ? (
      <td key={key} className={"num eo-drill " + cls}>
        <button type="button" className="eo-drill-btn" onClick={onOpenStaff}>{money(value)}</button>
      </td>
    ) : (
      <td key={key} className={"num " + cls}>{money(value)}</td>
    );

  const openItems = (month: number | null, focus: "sales" | "cogs") =>
    outlet && onOpenModal ? () => onOpenModal({ kind: "items", outlet, month, focus }) : null;
  const openFixed = (month: number | null, monthModel: MonthModel | null) =>
    outlet && onOpenModal ? () => onOpenModal({ kind: "fixed", outlet, month, monthModel }) : null;

  return (
    <div className="eo-scroll">
      <table className="eo-table eo-months">
        <thead>
          <tr>
            <th>&nbsp;</th>
            {months.map((m) => (
              <th key={m.month} className="num">{FISCAL_MONTHS[m.month]}</th>
            ))}
            <th className="num total">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Sales (net)</th>
            {months.map((m) => drill(m.month, money(m.net), openItems(m.month, "sales")))}
            {drill("t", money(total.net), openItems(null, "sales"), "total")}
          </tr>
          <tr>
            <th scope="row">Costs</th>
            {months.map((m) => (
              <td key={m.month} className="num">{money(costsOf(m))}</td>
            ))}
            <td className="num total">{money(total.costs)}</td>
          </tr>
          <tr className="eo-row-sub">
            <th scope="row">— cost of goods</th>
            {months.map((m) => drill(m.month, money(m.cogs), openItems(m.month, "cogs")))}
            {drill("t", money(total.cogs), openItems(null, "cogs"), "total")}
          </tr>
          {live ? (
            <>
              <tr className="eo-row-sub">
                <th scope="row">
                  — staff costs{" "}
                  <span className={"eo-chip " + (roster!.mode === "forecast" ? "alt" : "")}>
                    {roster!.mode === "forecast" ? "forecast" : "actual"}
                  </span>
                </th>
                {months.map((m) => staffCell(m.month, staffOf(m)))}
                {staffCell("t", total.staff, "total")}
              </tr>
              <tr className="eo-row-sub">
                <th scope="row">— fixed (utilities &amp; maintenance)</th>
                {months.map((m) => (
                  <td key={m.month} className="num">{money(otherOf(m))}</td>
                ))}
                <td className="num total">{money(total.other)}</td>
              </tr>
            </>
          ) : (
            <tr className="eo-row-sub">
              <th scope="row">— fixed costs</th>
              {months.map((m) => drill(m.month, money(m.fixed), openFixed(m.month, m)))}
              {drill("t", money(months.reduce((a, m) => a + m.fixed, 0)), openFixed(null, null), "total")}
            </tr>
          )}
          <tr>
            <th scope="row">Profit</th>
            {months.map((m) => {
              const p = profitOf(m);
              return (
                <td key={m.month} className={"num" + (p < 0 ? " neg" : " pos")}>{money(p)}</td>
              );
            })}
            <td className={"num total" + (total.profit < 0 ? " neg" : "")}>{money(total.profit)}</td>
          </tr>
          <tr className="eo-row-sub">
            <th scope="row">Margin</th>
            {months.map((m) => {
              const mg = marginPct({ net: m.net, profit: profitOf(m) });
              return (
                <td key={m.month} className={"num" + ((mg ?? 0) < 0 ? " neg" : "")}>{pct(mg)}</td>
              );
            })}
            <td className="num total">{pct(marginPct({ net: total.net, profit: total.profit }))}</td>
          </tr>
          <tr className="eo-row-sub">
            <th scope="row">Trading days</th>
            {months.map((m) => (
              <td key={m.month} className="num">{m.tradingDays}</td>
            ))}
            <td className="num total">{total.days}</td>
          </tr>
        </tbody>
      </table>
      <p className="eo-note">
        Month labels run March to February (the NYMR operating year {fiscalYear - 1}/
        {String(fiscalYear).slice(-2)}).
        {live && (
          <>
            {" "}Staff cost is the rostered <strong>employed-only</strong> labour from
            rostering-nymr (volunteers cost £0), split out of fixed costs — click a staff figure
            for the named rota. Fixed here is utilities &amp; maintenance only.
          </>
        )}
      </p>
    </div>
  );
}

function OutletView({
  outlet,
  fiscalYear,
  allHours,
  cogsSource,
  onOpenModal,
  roster,
  expandedMonth,
  onToggleMonth,
  dayRows,
  dayLoading,
  costAdjust,
}: {
  outlet: OutletModel;
  fiscalYear: number;
  allHours: boolean;
  cogsSource: CogsSource;
  onOpenModal: (m: ModalState) => void;
  roster?: RosterOverlay | null;
  expandedMonth: number | null;
  onToggleMonth: (m: number) => void;
  dayRows: ApiDayRow[] | undefined;
  dayLoading: boolean;
  costAdjust: number;
}) {
  const months = outlet.months;
  const expanded = months.find((m) => m.month === expandedMonth) ?? null;

  // Live Rostering: recompute the outlet KPIs from the split (rostered
  // employed-only staff + non-staff fixed) so the header agrees with the table.
  const live = !!(roster && roster.available);
  const period = `season ${fiscalYear - 1}/${String(fiscalYear).slice(-2)}`;
  const liveProfit = live
    ? outlet.net - outlet.cogs - roster!.otherFixedTotal - roster!.staffTotal
    : outlet.profit;
  const openStaff = live
    ? () =>
        onOpenModal({
          kind: "staff",
          titleName: outlet.location,
          period,
          mode: roster!.mode,
          staff: roster!.staff,
          totals: roster!.totals,
          hasUnratedRoles: roster!.hasUnratedRoles,
        })
    : null;

  // Per-hour staffing overlay for the by-hour cards (only when Live Rostering is
  // on and this outlet is mapped to a roster). perHour is the rostered average
  // concurrency; ratePerHour is employed-only so a volunteer roster shows £0.
  const staff = useMemo(() => buildStaffOverlay(roster, outlet.tradingHours), [roster, outlet.tradingHours]);

  const days: DayModel[] = useMemo(() => {
    if (!expanded || !dayRows) return [];
    return buildDayModels(dayRows, outlet, cogsSource, costAdjust, expanded.net);
  }, [expanded, dayRows, outlet, cogsSource, costAdjust]);

  return (
    <div className="eo-outlet">
      <div className="eo-outlet-head">
        <h3>
          {outlet.location}
          <span className="eo-chip">{outlet.category}</span>
          {outlet.costs.isOverride && <span className="eo-chip alt">custom costs</span>}
          {outlet.usesIplicitStaff && <span className="eo-chip alt">payroll staffing</span>}
          {live && (
            <span className={"eo-chip " + (roster!.mode === "forecast" ? "alt" : "")}>
              live rostering · {roster!.mode}
            </span>
          )}
        </h3>
        <div className="eo-kpis">
          <div className="eo-kpi">
            <span>Sales (net)</span>
            <strong>{money(outlet.net)}</strong>
          </div>
          <div className="eo-kpi">
            <span>Cost of goods</span>
            <strong>{money(outlet.cogs)}</strong>
          </div>
          {live ? (
            <>
              <div className="eo-kpi">
                <span>Fixed (util &amp; maint)</span>
                <strong>{money(roster!.otherFixedTotal)}</strong>
              </div>
              <div className="eo-kpi">
                <span>Staff (rostered)</span>
                <strong>{money(roster!.staffTotal)}</strong>
              </div>
              <div className={"eo-kpi" + (liveProfit < 0 ? " neg" : " pos")}>
                <span>Profit</span>
                <strong>{money(liveProfit)}</strong>
              </div>
              <div className="eo-kpi">
                <span>Margin</span>
                <strong>{pct(marginPct({ net: outlet.net, profit: liveProfit }))}</strong>
              </div>
              <div className="eo-kpi">
                <span>Staff hours</span>
                <strong>{roster!.hours.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="eo-kpi">
                <span>Fixed costs</span>
                <strong>{money(outlet.fixed)}</strong>
              </div>
              <div className={"eo-kpi" + (outlet.profit < 0 ? " neg" : " pos")}>
                <span>Profit</span>
                <strong>{money(outlet.profit)}</strong>
              </div>
              <div className="eo-kpi">
                <span>Margin</span>
                <strong>{pct(marginPct(outlet))}</strong>
              </div>
              <div className="eo-kpi">
                <span>Fixed / day</span>
                <strong>{moneyPence(outlet.fixedPerDay)}</strong>
              </div>
              {outlet.costs.staffHourly && (
                <div className="eo-kpi">
                  <span>Staffing / hour</span>
                  <strong>{moneyPence(outlet.staffPerHour)}</strong>
                </div>
              )}
            </>
          )}
        </div>
        {outlet.costs.staffHourly && (
          <p className="eo-note">
            Staffing is {outlet.costs.staffCount} × {moneyPence(outlet.costs.staffCostPerHour ?? 0)}/hr
            across {outlet.tradingHours.toLocaleString("en-GB")} trading hours ={" "}
            <strong>{money(outlet.staffPerHour * outlet.tradingHours)}</strong> — that is{" "}
            {outlet.net > 0
              ? `${((outlet.staffPerHour * outlet.tradingHours) / outlet.net * 100).toFixed(1)}% of net sales`
              : "not comparable to sales"}
            , replacing the {outlet.costs.staffing}% staffing figure.
          </p>
        )}
        {cogsSource === "actual" && (
          <p className="eo-note">
            {categoryHasActualCost(outlet.category) ? (
              <>
                Actual cost covers{" "}
                <strong className={(outlet.coveragePct ?? 0) < 60 ? "eo-cover-low" : undefined}>
                  {outlet.coveragePct?.toFixed(0) ?? "--"}%
                </strong>{" "}
                of net sales; the modelled {outlet.costs.cogs}% applies to the uncosted remainder.
              </>
            ) : (
              <>MERAC holds no item cost for tea rooms, so cost of goods stays modelled here.</>
            )}
          </p>
        )}
      </div>

      <ModellingPanel outlet={outlet} />

      <h4 className="eo-h4">
        Sales by month{" "}
        <span className="eo-h4-sub">
          {live
            ? "staff cost split out of fixed — click a staff figure for the named rota"
            : "click a sales, cost-of-goods or fixed-costs figure for the detail"}
        </span>
      </h4>
      <MonthTable
        months={months}
        fiscalYear={fiscalYear}
        outlet={outlet}
        onOpenModal={onOpenModal}
        roster={roster}
        onOpenStaff={openStaff}
      />

      <h4 className="eo-h4">
        Profit by month <span className="eo-h4-sub">zero line is breakeven</span>
      </h4>
      <BreakevenBars
        values={months.map((m) => m.profit)}
        labels={months.map((m) => fiscalMonthLabel(m.month, fiscalYear))}
        onBarClick={(i) => onToggleMonth(months[i].month)}
        activeIndex={expandedMonth == null ? undefined : months.findIndex((m) => m.month === expandedMonth)}
      />
      <div className="eo-axis" style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
        {months.map((m) => (
          <span key={m.month}>{FISCAL_MONTHS[m.month]}</span>
        ))}
      </div>

      <h4 className="eo-h4">
        Sales &amp; profit by hour
        <span className="eo-h4-sub">click the month header to open its days; click an hour bar for its detail</span>
      </h4>
      {staff && <StaffOverlayLegend staff={staff} />}
      <div className="eo-grid">
        {months.map((m) => {
          const folded = foldSlots(m.slots, allHours);
          const open = expandedMonth === m.month;
          const mStaffCost = roster?.staffByMonth.get(m.month) ?? 0;
          const mRag = staffingRag(mStaffCost, m.net - m.cogs);
          return (
            <div key={m.month} className={"eo-card static" + (open ? " open" : "")}>
              <button
                type="button"
                className="eo-card-head eo-card-toggle"
                onClick={() => onToggleMonth(m.month)}
                aria-expanded={open}
              >
                <strong>{FISCAL_MONTHS[m.month]} {open ? "▾" : "▸"}</strong>
                <span className={m.profit < 0 ? "neg" : "pos"}>{money(m.profit)}</span>
              </button>
              {staff && (
                <StaffBadge
                  rag={mRag}
                  label={`≈${Math.max(1, Math.round(staff.perHour))} on shift`}
                  title={`${fiscalMonthLabel(m.month, fiscalYear)}: rostered labour ${money(mStaffCost)} vs gross margin ${money(m.net - m.cogs)} (employed-only). ≈${staff.perHour.toFixed(1)} staff/hr on average.`}
                />
              )}
              {staff && <StaffPerHourRow slots={folded} perHour={staff.perHour} />}
              <SalesProfitBars
                slots={folded}
                compact
                onSlotClick={(s) =>
                  onOpenModal({
                    kind: "slot",
                    titleName: outlet.location,
                    locations: [outlet.location],
                    slot: s,
                    label: `${fiscalMonthLabel(m.month, fiscalYear)} · this hour across the month`,
                    month: m.month,
                    fiscalYear,
                  })
                }
              />
              <SlotAxis slots={folded} />
            </div>
          );
        })}
      </div>

      {expanded && (
        <div className="eo-days">
          <h4 className="eo-h4">
            {fiscalMonthLabel(expanded.month, fiscalYear)} — by day
            <span className="eo-h4-sub">each chart is one day, hour by hour</span>
          </h4>
          {dayLoading && <p className="eo-muted">Loading days...</p>}
          {!dayLoading && !days.length && <p className="eo-muted">No trade recorded this month.</p>}
          <div className="eo-grid days">
            {days.map((d) => {
              const folded = foldSlots(d.slots, allHours);
              const dt = new Date(`${d.date}T00:00:00Z`);
              const dTrading = folded.filter((s) => s.net > 0 || s.transactions > 0).length;
              const dStaffCost = staff ? staff.ratePerHour * staff.perHour * dTrading : 0;
              const dRag = staffingRag(dStaffCost, d.net - d.cogs);
              return (
                <div key={d.date} className={"eo-card static" + (d.profit < 0 ? " loss" : "")}>
                  <div className="eo-card-head">
                    <strong>
                      {dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" })}
                    </strong>
                    <span className={d.profit < 0 ? "neg" : "pos"}>{money(d.profit)}</span>
                  </div>
                  {staff && (
                    <StaffBadge
                      rag={dRag}
                      label={`≈${Math.max(1, Math.round(staff.perHour))} on shift`}
                      title={`${dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}: ≈${staff.perHour.toFixed(1)} rostered staff/hr over ${dTrading} trading hour${dTrading === 1 ? "" : "s"} = ${money(dStaffCost)} employed-only labour vs gross margin ${money(d.net - d.cogs)}.`}
                    />
                  )}
                  {staff && <StaffPerHourRow slots={folded} perHour={staff.perHour} />}
                  <SalesProfitBars
                    slots={folded}
                    compact
                    height={84}
                    onSlotClick={(s) =>
                      onOpenModal({
                        kind: "slot",
                        titleName: outlet.location,
                        locations: [outlet.location],
                        slot: s,
                        label: dt.toLocaleDateString("en-GB", {
                          weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
                        }),
                        date: d.date,
                        fiscalYear,
                      })
                    }
                  />
                  <SlotAxis slots={folded} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  All-outlets summary
// ---------------------------------------------------------------------------

function SummaryView({
  outlets,
  totals,
  fiscalYear,
  allHours,
  cogsSource,
  title,
  subtitle,
  onOpenModal,
  roster,
}: {
  outlets: OutletModel[];
  totals: Bucket;
  fiscalYear: number;
  allHours: boolean;
  cogsSource: CogsSource;
  title: string;
  subtitle: string;
  onOpenModal: (m: ModalState) => void;
  roster?: RosterOverlay | null;
}) {
  const scopeLocations = outlets.map((o) => o.location);

  // Live Rostering: only split when every outlet in the scope is mapped to a
  // roster (a summary that mixes rostered and model-staffed outlets would
  // understate cost). Recompute the summary KPIs from the split when it applies.
  const live = !!(roster && roster.available);
  const period = `season ${fiscalYear - 1}/${String(fiscalYear).slice(-2)}`;
  const liveProfit = live
    ? totals.net - totals.cogs - roster!.otherFixedTotal - roster!.staffTotal
    : totals.profit;
  const openStaff = live
    ? () =>
        onOpenModal({
          kind: "staff",
          titleName: title,
          period,
          mode: roster!.mode,
          staff: roster!.staff,
          totals: roster!.totals,
          hasUnratedRoles: roster!.hasUnratedRoles,
        })
    : null;
  // Per-hour staffing overlay across the whole scope (all its outlets' trading
  // hours combined). Employed-only, so a volunteer-heavy scope reads £0.
  const scopeTradingHours = useMemo(
    () => outlets.reduce((a, o) => a + o.tradingHours, 0),
    [outlets]
  );
  const staff = useMemo(() => buildStaffOverlay(roster, scopeTradingHours), [roster, scopeTradingHours]);
  // Combine every outlet's months and season slots.
  const months: MonthModel[] = useMemo(() => {
    const by = new Map<number, MonthModel>();
    for (const o of outlets) {
      for (const m of o.months) {
        const cur =
          by.get(m.month) ??
          ({
            month: m.month,
            tradingDays: 0,
            slots: [],
            net: 0, cogs: 0, fixed: 0, profit: 0, costedNet: 0, items: 0, transactions: 0,
            dayCount: 0,
          } as MonthModel);
        cur.net += m.net; cur.cogs += m.cogs; cur.fixed += m.fixed; cur.profit += m.profit;
        cur.costedNet += m.costedNet; cur.items += m.items; cur.transactions += m.transactions;
        // trading days across outlets are not additive as "days open" — the max
        // is the number of days the estate traded at all.
        cur.tradingDays = Math.max(cur.tradingDays, m.tradingDays);
        cur.slots = cur.slots.concat(m.slots);
        by.set(m.month, cur);
      }
    }
    const list = Array.from(by.values()).sort((a, b) => a.month - b.month);
    // collapse each month's concatenated slots back onto one per hour
    return list.map((m) => ({ ...m, slots: foldSlots(m.slots, true) }));
  }, [outlets]);

  const seasonSlots = useMemo(() => {
    const all: SlotBucket[] = [];
    for (const o of outlets) all.push(...o.slots);
    return foldSlots(all, allHours);
  }, [outlets, allHours]);

  return (
    <div className="eo-outlet">
      <div className="eo-outlet-head">
        <h3>
          {title}<span className="eo-chip">{subtitle}</span>
          {live && (
            <span className={"eo-chip " + (roster!.mode === "forecast" ? "alt" : "")}>
              live rostering · {roster!.mode}
            </span>
          )}
        </h3>
        <div className="eo-kpis">
          <div className="eo-kpi"><span>Sales (net)</span><strong>{money(totals.net)}</strong></div>
          <div className="eo-kpi"><span>Cost of goods</span><strong>{money(totals.cogs)}</strong></div>
          {live ? (
            <>
              <div className="eo-kpi"><span>Fixed (util &amp; maint)</span><strong>{money(roster!.otherFixedTotal)}</strong></div>
              <div className="eo-kpi"><span>Staff (rostered)</span><strong>{money(roster!.staffTotal)}</strong></div>
              <div className={"eo-kpi" + (liveProfit < 0 ? " neg" : " pos")}>
                <span>Profit</span><strong>{money(liveProfit)}</strong>
              </div>
              <div className="eo-kpi"><span>Margin</span><strong>{pct(marginPct({ net: totals.net, profit: liveProfit }))}</strong></div>
            </>
          ) : (
            <>
              <div className="eo-kpi"><span>Fixed costs</span><strong>{money(totals.fixed)}</strong></div>
              <div className={"eo-kpi" + (totals.profit < 0 ? " neg" : " pos")}>
                <span>Profit</span><strong>{money(totals.profit)}</strong>
              </div>
              <div className="eo-kpi"><span>Margin</span><strong>{pct(marginPct(totals))}</strong></div>
            </>
          )}
        </div>
        <p className="eo-note">
          Cost of goods source: <strong>{cogsSource === "actual" ? "actual (blended)" : "modelled %"}</strong>.
        </p>
      </div>

      <h4 className="eo-h4">Sales by month — {title.toLowerCase()}</h4>
      <MonthTable months={months} fiscalYear={fiscalYear} roster={roster} onOpenStaff={openStaff} />

      <h4 className="eo-h4">
        Profit by month <span className="eo-h4-sub">zero line is breakeven</span>
      </h4>
      <BreakevenBars
        values={months.map((m) => m.profit)}
        labels={months.map((m) => fiscalMonthLabel(m.month, fiscalYear))}
      />
      <div className="eo-axis" style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
        {months.map((m) => (<span key={m.month}>{FISCAL_MONTHS[m.month]}</span>))}
      </div>

      <h4 className="eo-h4">
        Sales &amp; profit by hour — whole season <span className="eo-h4-sub">click an hour bar for the sales behind it</span>
      </h4>
      {staff && <StaffOverlayLegend staff={staff} />}
      {staff && <StaffPerHourRow slots={seasonSlots} perHour={staff.perHour} />}
      <SalesProfitBars
        slots={seasonSlots}
        height={150}
        onSlotClick={(s) =>
          onOpenModal({
            kind: "slot", titleName: title, locations: scopeLocations, slot: s,
            label: `${subtitle} · this hour, whole season`, fiscalYear,
          })
        }
      />
      <SlotAxis slots={seasonSlots} />

      <h4 className="eo-h4">Sales &amp; profit by hour — each month</h4>
      <div className="eo-grid">
        {months.map((m) => {
          const folded = foldSlots(m.slots, allHours);
          const mStaffCost = roster?.staffByMonth.get(m.month) ?? 0;
          const mRag = staffingRag(mStaffCost, m.net - m.cogs);
          return (
            <div key={m.month} className="eo-card static">
              <div className="eo-card-head">
                <strong>{FISCAL_MONTHS[m.month]}</strong>
                <span className={m.profit < 0 ? "neg" : "pos"}>{money(m.profit)}</span>
              </div>
              {staff && (
                <StaffBadge
                  rag={mRag}
                  label={`≈${Math.max(1, Math.round(staff.perHour))} on shift`}
                  title={`${fiscalMonthLabel(m.month, fiscalYear)}: rostered labour ${money(mStaffCost)} vs gross margin ${money(m.net - m.cogs)} (employed-only). ≈${staff.perHour.toFixed(1)} staff/hr on average.`}
                />
              )}
              {staff && <StaffPerHourRow slots={folded} perHour={staff.perHour} />}
              <SalesProfitBars
                slots={folded}
                compact
                onSlotClick={(s) =>
                  onOpenModal({
                    kind: "slot", titleName: title, locations: scopeLocations, slot: s,
                    label: `${subtitle} · ${fiscalMonthLabel(m.month, fiscalYear)}, this hour`,
                    month: m.month, fiscalYear,
                  })
                }
              />
              <SlotAxis slots={folded} />
            </div>
          );
        })}
      </div>

      <h4 className="eo-h4">Outlet league</h4>
      <div className="eo-scroll">
        <table className="eo-table">
          <thead>
            <tr>
              <th>Outlet</th><th>Category</th>
              <th className="num">Sales (net)</th><th className="num">COGS</th>
              <th className="num">Fixed</th><th className="num">Profit</th><th className="num">Margin</th>
            </tr>
          </thead>
          <tbody>
            {[...outlets].sort((a, b) => b.profit - a.profit).map((o) => (
              <tr key={`${o.category}|${o.location}`}>
                <td>{o.location}</td>
                <td>{o.category}</td>
                <td className="num">{money(o.net)}</td>
                <td className="num">{money(o.cogs)}</td>
                <td className="num">{money(o.fixed)}</td>
                <td className={"num" + (o.profit < 0 ? " neg" : " pos")}>{money(o.profit)}</td>
                <td className={"num" + ((marginPct(o) ?? 0) < 0 ? " neg" : "")}>{pct(marginPct(o))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Opening-times optimiser
// ---------------------------------------------------------------------------

function OptimiserPanel({
  outlet,
  fiscalYear,
  cogsSource,
  costAdjustments,
}: {
  outlet: OutletModel | null;
  fiscalYear: number;
  cogsSource: CogsSource;
  costAdjustments: { month: number; location: string; cost_adjust: number }[];
}) {
  const [maxStaff, setMaxStaff] = useState("2");
  const [minPerDay, setMinPerDay] = useState("0");
  const [minPerMonth, setMinPerMonth] = useState("0");
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<OptimiseResult | null>(null);
  const [baseline, setBaseline] = useState<{ current: number; lastYear: number | null } | null>(null);
  const [ranFor, setRanFor] = useState<string | null>(null);
  const [resultYear, setResultYear] = useState<number>(fiscalYear);

  const key = outlet ? `${outlet.category}|${outlet.location}` : null;
  // Invalidate a stale result when the outlet changes.
  useEffect(() => {
    setResult(null);
    setBaseline(null);
    setRanFor(null);
  }, [key]);

  // Fetch a season, build the model for THIS outlet, and its day data + cost
  // adjustments. Used both for the current season and for the last-year run.
  const loadYear = async (fy: number): Promise<{
    om: OutletModel;
    dayRowsByMonth: Record<number, ApiDayRow[]>;
    monthNet: Record<number, number>;
    costAdjust: Record<number, number>;
    actual: number;
  } | null> => {
    if (!outlet) return null;
    let om = outlet;
    let adjustments = costAdjustments;
    if (fy !== fiscalYear) {
      const res = await fetch(`/api/dashboards/exec/outlets?fiscalYear=${fy}`, { credentials: "include" });
      if (!res.ok) throw new Error(`season ${fy}: ${res.status}`);
      const data = (await res.json()) as ApiSeason;
      const found = buildOutletModels(data, cogsSource).find(
        (o) => o.category === outlet.category && o.location === outlet.location
      );
      if (!found) return null;
      om = found;
      adjustments = data.cost_adjustments;
    }
    const dayRowsByMonth: Record<number, ApiDayRow[]> = {};
    await Promise.all(
      om.months.map(async (m) => {
        const res = await fetch(
          `/api/dashboards/exec/outlets/days?fiscalYear=${fy}&month=${m.month}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`days ${m.month}: ${res.status}`);
        const j = (await res.json()) as { rows: ApiDayRow[] };
        dayRowsByMonth[m.month] = j.rows;
      })
    );
    const monthNet: Record<number, number> = {};
    const costAdjust: Record<number, number> = {};
    for (const m of om.months) monthNet[m.month] = m.net;
    for (const a of adjustments) if (a.location === om.location) costAdjust[a.month] = a.cost_adjust;
    return { om, dayRowsByMonth, monthNet, costAdjust, actual: om.profit };
  };

  const run = async (targetFy: number) => {
    if (!outlet) return;
    const maxStaffN = Number(maxStaff);
    if (!Number.isFinite(maxStaffN) || maxStaffN <= 0) {
      setErr("Enter a Max Paid Staff of 1 or more.");
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const target = await loadYear(targetFy);
      if (!target) {
        setErr(targetFy === fiscalYear ? "No data for this outlet." : "No data for this outlet last year.");
        return;
      }
      const res = optimiseOutlet(
        target.om, target.dayRowsByMonth, target.monthNet, target.costAdjust, cogsSource,
        { maxStaff: maxStaffN, ratePerHour: modelRate(target.om), minPerDay: Number(minPerDay) || 0, minPerMonth: Number(minPerMonth) || 0 }
      );
      setResult(res);
      setResultYear(targetFy);

      // Compare the optimised year to that year's own actual, and to the year
      // before over the same months.
      let priorActual: number | null = null;
      try {
        const py = await fetch(`/api/dashboards/exec/outlets?fiscalYear=${targetFy - 1}`, { credentials: "include" });
        if (py.ok) {
          const pyData = (await py.json()) as ApiSeason;
          const pyOutlet = buildOutletModels(pyData, cogsSource).find(
            (o) => o.category === outlet.category && o.location === outlet.location
          );
          if (pyOutlet) {
            const monthsPresent = new Set(target.om.months.map((m) => m.month));
            priorActual = pyOutlet.months.filter((m) => monthsPresent.has(m.month)).reduce((a, m) => a + m.profit, 0);
          }
        }
      } catch { /* best-effort */ }
      setBaseline({ current: target.actual, lastYear: priorActual });
      setRanFor(outlet.location);
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="eo-panel eo-optim">
      <div className="eo-panel-head static">
        <h3>Optimise opening times</h3>
        {outlet && <span className="eo-panel-hint">{outlet.location} · {outlet.category}</span>}
      </div>
      <div className="eo-panel-body">
        {!outlet && (
          <p className="eo-muted">Pick a single outlet tab to model its opening pattern.</p>
        )}
        {outlet && (
          <>
            <div className="eo-optim-inputs">
              <label>
                <span>Max paid staff</span>
                <input type="number" min={1} step={1} value={maxStaff} onChange={(e) => setMaxStaff(e.target.value)} />
                <em>costs modelled at this headcount</em>
              </label>
              <label>
                <span>Min profit to open — per day £</span>
                <input type="number" step={10} value={minPerDay} onChange={(e) => setMinPerDay(e.target.value)} />
                <em>a day below this closes</em>
              </label>
              <label>
                <span>Min profit to open — per month £</span>
                <input type="number" step={100} value={minPerMonth} onChange={(e) => setMinPerMonth(e.target.value)} />
                <em>a month below this closes</em>
              </label>
              <button type="button" className="eo-btn" disabled={running} onClick={() => void run(fiscalYear)}>
                {running ? "Optimising..." : "Optimise"}
              </button>
              <button type="button" className="eo-btn ghost" disabled={running} onClick={() => void run(fiscalYear - 1)}
                title="Run the optimiser over the full previous season — a complete 12 months, so you see the whole-year picture.">
                Optimise against last year
              </button>
              {result && (
                <button
                  type="button"
                  className="eo-btn ghost"
                  onClick={() =>
                    exportOptimiserPdf({
                      outlet, fiscalYearLabel: `${resultYear - 1}/${String(resultYear).slice(-2)}`,
                      maxStaff: Number(maxStaff) || 0, minPerDay: Number(minPerDay) || 0,
                      minPerMonth: Number(minPerMonth) || 0, result, baseline,
                    })
                  }
                >
                  PDF
                </button>
              )}
            </div>
            {err && <p className="eo-error">{err}</p>}
            <p className="eo-note">
              Opening is a marginal call: closing saves staffing and cost of goods, but utilities,
              maintenance and management are committed and still land in the year total. "Profit to
              open" is therefore sales less cost of goods less staffing (at {maxStaff} staff x{" "}
              {moneyPence(outlet ? modelRate(outlet) : 0)}/hr, the outlet's rate).
            </p>

            {result && ranFor === outlet.location && (
              <>
                {resultYear !== fiscalYear && (
                  <p className="eo-optim-yearflag">
                    Optimised against <strong>last year in full ({resultYear - 1}/{String(resultYear).slice(-2)})</strong> — a complete 12-month season.
                  </p>
                )}
                <OptimiserResult result={result} baseline={baseline} fiscalYear={resultYear} />
                <OptimiserCalendar result={result} fiscalYear={resultYear} />
              </>
            )}

            <LeanStaffingBlock outlet={outlet} fiscalYear={fiscalYear} />
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Lean Staffing — staff to demand off the rostered pattern, then trim edges.
//
//  Reads the outlet's rostered staff pattern from rostering-nymr (employed-only
//  cost, so a volunteer's hours cost £0) and the season's intraday transaction
//  loading, then recommends a headcount per hour and a trimmed opening window,
//  with the £ saving vs the current roster. Falls back to the outlet's cost
//  model if it is not mapped to a rostered unit, so the pill still works.
// ---------------------------------------------------------------------------

interface LeanBasis {
  source: "roster" | "model";
  baselineHeadcount: number;
  ratePerStaffHour: number;
  rosterHours: number;
  rosterCost: number;
  allVolunteers: boolean;
}

function LeanStaffingBlock({ outlet, fiscalYear }: { outlet: OutletModel; fiscalYear: number }) {
  const traded = useMemo(
    () => outlet.slots.filter((s) => s.dayCount > 0).sort((a, b) => a.slot - b.slot),
    [outlet]
  );
  const peakTxPerHour = useMemo(
    () => traded.reduce((mx, s) => Math.max(mx, s.dayCount > 0 ? s.transactions / s.dayCount : 0), 0),
    [traded]
  );
  const defaultThreshold = Math.max(1, Math.round(peakTxPerHour * 0.4));

  const [roster, setRoster] = useState<RLOutlet | null>(null);
  const [rosterBusy, setRosterBusy] = useState(false);
  const [rosterErr, setRosterErr] = useState<string | null>(null);
  const [tx, setTx] = useState<number>(defaultThreshold);
  const [guard, setGuard] = useState("2");
  const [ran, setRan] = useState(false);

  const key = `${outlet.category}|${outlet.location}`;
  useEffect(() => {
    setRoster(null);
    setRosterErr(null);
    setRan(false);
    setTx(Math.max(1, Math.round(peakTxPerHour * 0.4)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const useRosterPattern = async () => {
    setRosterBusy(true);
    setRosterErr(null);
    try {
      const res = await fetch(`/api/dashboards/exec/rostering-labour?fiscalYear=${fiscalYear}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`rostering feed: ${res.status}`);
      const feed = (await res.json()) as RLFeed;
      if (feed.configured === false) {
        setRosterErr("The rostering link is not configured for this environment — using the cost model instead.");
        setRoster(null);
        return;
      }
      const mine = feed.outlets.find(
        (o) => o.category === outlet.category && o.location === outlet.location
      );
      if (!mine || !mine.available) {
        setRosterErr(`${outlet.location} is not mapped to a rostered unit — using the cost model instead.`);
        setRoster(null);
        return;
      }
      setRoster(mine);
    } catch (e) {
      setRosterErr(String((e as Error)?.message ?? e) + " — using the cost model instead.");
      setRoster(null);
    } finally {
      setRosterBusy(false);
    }
  };

  // The staffing basis: rostered pattern when loaded and available, otherwise
  // the outlet's cost model. baselineHeadcount is average concurrency per open
  // hour; the rate is EMPLOYED-ONLY (£0 for an all-volunteer roster).
  const basis: LeanBasis = useMemo(() => {
    if (roster) {
      const rosterHours = roster.totals.hours;
      const rosterCost = roster.totals.cost;
      const baselineHeadcount = outlet.tradingHours > 0 ? rosterHours / outlet.tradingHours : 0;
      const ratePerStaffHour = rosterHours > 0 ? rosterCost / rosterHours : 0;
      return {
        source: "roster",
        baselineHeadcount,
        ratePerStaffHour,
        rosterHours,
        rosterCost,
        allVolunteers: rosterHours > 0 && rosterCost === 0,
      };
    }
    return {
      source: "model",
      baselineHeadcount: defaultStaffCount(outlet),
      ratePerStaffHour: modelRate(outlet),
      rosterHours: 0,
      rosterCost: 0,
      allVolunteers: false,
    };
  }, [roster, outlet]);

  const result: LeanStaffingResult | null = useMemo(() => {
    if (!ran || !traded.length) return null;
    return leanStaffing(outlet.slots, {
      txPerHourThreshold: tx,
      maxSalesLossPct: Number(guard) || 0,
      baselineHeadcount: basis.baselineHeadcount,
      ratePerStaffHour: basis.ratePerStaffHour,
    });
  }, [ran, traded.length, outlet, tx, guard, basis]);

  if (!traded.length) return null;

  const sliderMax = Math.max(5, Math.ceil(peakTxPerHour));

  return (
    <div className="eo-lean">
      <h4 className="eo-h4">
        Lean staffing <span className="eo-h4-sub">staff to demand off the rostered pattern, then trim the quiet edges</span>
      </h4>

      <div className="eo-lean-controls">
        <button
          type="button"
          className={"eo-btn " + (basis.source === "roster" ? "" : "ghost")}
          disabled={rosterBusy}
          onClick={() => void useRosterPattern()}
          title="Load this outlet's rostered staff pattern (employed-only cost) from rostering-nymr as the baseline."
        >
          {rosterBusy
            ? "Loading roster..."
            : basis.source === "roster"
            ? "Rostering pattern loaded ✓"
            : "Use Rostering Staff Patterns"}
        </button>

        <label className="eo-lean-slider">
          <span>
            Transactions / hour to keep full staff: <strong>{tx}</strong>
          </span>
          <input
            type="range"
            min={0}
            max={sliderMax}
            step={1}
            value={tx}
            onChange={(e) => setTx(Number(e.target.value))}
          />
          <em>hours below this are staffed down in proportion (peak here is {peakTxPerHour.toFixed(1)}/hr)</em>
        </label>

        <label className="eo-lean-guard">
          <span>Max sales at risk %</span>
          <input type="number" min={0} max={100} step={0.5} value={guard} onChange={(e) => setGuard(e.target.value)} />
          <em>edges only close within this</em>
        </label>

        <button
          type="button"
          className={"eo-pill " + (ran ? "active" : "")}
          onClick={() => setRan(true)}
        >
          Run Lean Staffing
        </button>
      </div>

      {rosterErr && <p className="eo-note eo-lean-fallback">{rosterErr}</p>}

      <p className="eo-note">
        Baseline: {basis.source === "roster" ? "rostered pattern" : "cost model"} —{" "}
        {basis.baselineHeadcount.toFixed(1)} staff/hr on average
        {basis.source === "roster"
          ? ` (${Math.round(basis.rosterHours).toLocaleString("en-GB")} rostered hrs, ${moneyPence(basis.ratePerStaffHour)}/hr employed-only)`
          : ` at ${moneyPence(basis.ratePerStaffHour)}/hr (implied — no rostered unit mapped)`}
        . Closing an hour assumes its takings are lost (worst case); staff cost is employed-only, so
        volunteer hours cost £0.
      </p>

      {basis.source === "roster" && basis.allVolunteers && (
        <p className="eo-note eo-lean-fallback">
          This roster is entirely volunteers, so its employed cost is £0 — the plan can cut hours but
          the cash saving is nil.
        </p>
      )}

      {result && <LeanStaffingResultView result={result} basis={basis} />}
    </div>
  );
}

function LeanStaffingResultView({ result, basis }: { result: LeanStaffingResult; basis: LeanBasis }) {
  const window =
    result.openFrom == null || result.openTo == null
      ? "closed all day"
      : `${slotShortLabel(result.openFrom)}–${slotShortLabel(result.openTo + 60)}`;
  return (
    <>
      <div className="eo-optim-cards">
        <div className={"eo-kpi" + (result.netSaving < 0 ? " neg" : " pos")}>
          <span>Saving vs current roster</span>
          <strong>{(result.netSaving >= 0 ? "+" : "") + money(result.netSaving)}</strong>
        </div>
        <div className="eo-kpi">
          <span>Labour saved</span>
          <strong>{money(result.laborSaving)}</strong>
        </div>
        <div className="eo-kpi">
          <span>Staff-hours saved</span>
          <strong>
            {Math.round(result.baselineHours - result.recommendedHours).toLocaleString("en-GB")}
            <span className="eo-lean-of"> of {Math.round(result.baselineHours).toLocaleString("en-GB")}</span>
          </strong>
        </div>
        <div className="eo-kpi">
          <span>Sales given up</span>
          <strong>{money(result.salesLost)}<span className="eo-lean-of"> · {pct(result.salesLostPct)}</span></strong>
        </div>
        <div className="eo-kpi">
          <span>Margin at risk</span>
          <strong>{money(result.marginLost)}</strong>
        </div>
        <div className="eo-kpi">
          <span>Recommended window</span>
          <strong>{window}{result.closedHours ? ` · ${result.closedHours} hr shut` : ""}</strong>
        </div>
      </div>

      <div className="eo-scroll">
        <table className="eo-table">
          <thead>
            <tr>
              <th>Hour</th>
              <th className="num">Trx / hr</th>
              <th className="num">Now</th>
              <th className="num">Lean</th>
              <th className="num">Sales</th>
              <th className="num">Saving</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {result.hours.map((h) => (
              <tr key={h.slot} className={h.open ? undefined : "eo-optim-closed"}>
                <td>{slotLabel(h.slot)}</td>
                <td className="num">{h.txPerHour.toFixed(1)}</td>
                <td className="num">{h.baselineHead.toFixed(1)}</td>
                <td className="num">{h.open ? h.recommendedHead.toFixed(1) : "—"}</td>
                <td className="num">{money(h.net)}</td>
                <td className="num pos">{h.saving > 0 ? money(h.saving) : "—"}</td>
                <td>
                  {!h.open
                    ? "close"
                    : h.recommendedHead < h.baselineHead - 0.05
                    ? `staff down`
                    : "keep"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="eo-model-profit">
              <th scope="row">Total</th>
              <td className="num">—</td>
              <td className="num">{money(result.baselineStaffCost)}</td>
              <td className="num">{money(result.recommendedStaffCost)}</td>
              <td className="num">{money(result.totalSales)}</td>
              <td className="num pos">{money(result.laborSaving)}</td>
              <td>—</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="eo-note">
        "Now" and "Lean" are average staff on shift per hour (the roster is a season, not one day).
        Cost columns under the totals are the season labour spend at{" "}
        {basis.source === "roster" ? "the rostered" : "the modelled"} rate. "Saving vs current roster"
        is the labour saved less the gross margin given up by the hours the guard allows to close.
      </p>
    </>
  );
}

function OptimiserResult({
  result, baseline, fiscalYear,
}: {
  result: OptimiseResult;
  baseline: { current: number; lastYear: number | null } | null;
  fiscalYear: number;
}) {
  const upliftCurrent = baseline ? result.year.profit - baseline.current : null;
  const upliftLast = baseline && baseline.lastYear != null ? result.year.profit - baseline.lastYear : null;
  return (
    <>
      <div className="eo-optim-cards">
        <div className="eo-kpi">
          <span>Optimised profit</span>
          <strong className={result.year.profit < 0 ? "neg" : "pos"}>{money(result.year.profit)}</strong>
        </div>
        <div className="eo-kpi">
          <span>Actual (that season)</span>
          <strong>{baseline ? money(baseline.current) : "--"}</strong>
        </div>
        <div className="eo-kpi">
          <span>Uplift vs actual</span>
          <strong className={(upliftCurrent ?? 0) < 0 ? "neg" : "pos"}>
            {upliftCurrent == null ? "--" : (upliftCurrent >= 0 ? "+" : "") + money(upliftCurrent)}
          </strong>
        </div>
        <div className="eo-kpi">
          <span>Prior year (same months)</span>
          <strong>{baseline?.lastYear != null ? money(baseline.lastYear) : "n/a"}</strong>
        </div>
        <div className="eo-kpi">
          <span>Uplift vs prior year</span>
          <strong className={(upliftLast ?? 0) < 0 ? "neg" : "pos"}>
            {upliftLast == null ? "n/a" : (upliftLast >= 0 ? "+" : "") + money(upliftLast)}
          </strong>
        </div>
        <div className="eo-kpi">
          <span>Closed</span>
          <strong>{result.monthsClosed} mo · {result.daysClosed} days</strong>
        </div>
      </div>

      <div className="eo-scroll">
        <table className="eo-table">
          <thead>
            <tr>
              <th>Month</th><th>Status</th>
              <th className="num">Sales</th><th className="num">COGS</th>
              <th className="num">Staffing</th><th className="num">Fixed</th>
              <th className="num">Profit</th><th className="num">Margin</th>
            </tr>
          </thead>
          <tbody>
            {result.months.map((m) => (
              <tr key={m.month} className={m.open ? undefined : "eo-optim-closed"}>
                <td>{fiscalMonthLabel(m.month, fiscalYear)}</td>
                <td>{m.open ? `open · ${m.daysOpen}d${m.daysClosed ? ` (${m.daysClosed} shut)` : ""}` : "closed"}</td>
                <td className="num">{money(m.sales)}</td>
                <td className="num">{money(m.cogs)}</td>
                <td className="num">{money(m.staffing)}</td>
                <td className="num">{money(m.fixed)}</td>
                <td className={"num" + (m.profit < 0 ? " neg" : " pos")}>{money(m.profit)}</td>
                <td className="num">{pct(m.marginPct)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="eo-model-profit">
              <th scope="row">Year</th><td>—</td>
              <td className="num">{money(result.year.sales)}</td>
              <td className="num">{money(result.year.cogs)}</td>
              <td className="num">{money(result.year.staffing)}</td>
              <td className="num">{money(result.year.fixed)}</td>
              <td className={"num" + (result.year.profit < 0 ? " neg" : "")}>{money(result.year.profit)}</td>
              <td className="num">{pct(result.year.marginPct)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="eo-note">
        Sales, COGS and staffing above are the OPEN days only; fixed overhead is the full month
        (incurred whether open or shut). Uplift compares the optimised year to that season's actual,
        and to the prior year over the same months. It is a projection on historic trade,
        not a forecast — closing an hour assumes that hour's takings are lost, the worst case.
      </p>
    </>
  );
}

// Calendar of the optimised open/closed pattern — clear month tiles, with the
// closed days listed under each open month.
function OptimiserCalendar({ result, fiscalYear }: { result: OptimiseResult; fiscalYear: number }) {
  const fmtDay = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return (
    <div className="eo-cal">
      <h4 className="eo-h4">Opening calendar <span className="eo-h4-sub">green = open, red = closed for the month</span></h4>
      <div className="eo-cal-grid">
        {result.months.map((m) => (
          <div key={m.month} className={"eo-cal-month " + (m.open ? "open" : "closed")}>
            <div className="eo-cal-head">
              <strong>{fiscalMonthLabel(m.month, fiscalYear)}</strong>
              <span className="eo-cal-badge">{m.open ? "OPEN" : "CLOSED"}</span>
            </div>
            {m.open ? (
              <>
                <div className="eo-cal-sub">
                  {m.daysOpen} day{m.daysOpen === 1 ? "" : "s"} open
                  {m.daysClosed ? ` · ${m.daysClosed} closed` : " · none closed"}
                </div>
                {m.closedDates.length > 0 && (
                  <div className="eo-cal-closed">
                    <span>Close:</span>
                    <div className="eo-cal-days">
                      {m.closedDates.map((d) => (
                        <span key={d} className="eo-cal-day">{fmtDay(d)}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="eo-cal-sub">Whole month shut ({m.daysClosed} trading day{m.daysClosed === 1 ? "" : "s"})</div>
            )}
          </div>
        ))}
      </div>
      <p className="eo-note">
        A day is closed when its sales less cost of goods less staffing falls below your per-day
        minimum; a month is closed when even its best open days can't clear the per-month minimum.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LossesView({
  outlets,
  fiscalYear,
  month,
  onMonth,
  dayRows,
  loading,
  cogsSource,
  allHours,
  costAdjustments,
}: {
  outlets: OutletModel[];
  fiscalYear: number;
  month: number | null;
  onMonth: (m: number | null) => void;
  dayRows: ApiDayRow[] | undefined;
  loading: boolean;
  cogsSource: CogsSource;
  allHours: boolean;
  costAdjustments: { month: number; location: string; cost_adjust: number }[];
}) {
  const monthsAvailable = useMemo(() => {
    const s = new Set<number>();
    for (const o of outlets) for (const m of o.months) s.add(m.month);
    return Array.from(s).sort((a, b) => a - b);
  }, [outlets]);

  const losses = useMemo(() => {
    if (month == null || !dayRows) return null;
    const lossDays: { outlet: string; category: string; date: string; net: number; profit: number }[] = [];
    const lossHours: {
      outlet: string; category: string; date: string; slot: number; net: number; profit: number;
    }[] = [];
    const bySlot = new Map<number, { net: number; profit: number; count: number }>();

    for (const o of outlets) {
      const m = o.months.find((x) => x.month === month);
      if (!m) continue;
      const adj = costAdjustments.find((a) => a.location === o.location && a.month === month)?.cost_adjust ?? 0;
      const days = buildDayModels(dayRows, o, cogsSource, adj, m.net);
      for (const d of days) {
        if (d.profit < 0) {
          lossDays.push({ outlet: o.location, category: o.category, date: d.date, net: d.net, profit: d.profit });
        }
        for (const s of d.slots) {
          if (s.profit < 0) {
            lossHours.push({
              outlet: o.location, category: o.category, date: d.date,
              slot: s.slot, net: s.net, profit: s.profit,
            });
          }
          const cur = bySlot.get(s.slot) ?? { net: 0, profit: 0, count: 0 };
          cur.net += s.net; cur.profit += s.profit; cur.count += 1;
          bySlot.set(s.slot, cur);
        }
      }
    }
    lossDays.sort((a, b) => a.profit - b.profit);
    lossHours.sort((a, b) => a.profit - b.profit);
    const slotAgg: SlotBucket[] = Array.from(bySlot.entries())
      .map(([slot, v]) => ({
        slot, net: v.net, profit: v.profit,
        cogs: 0, fixed: 0, costedNet: 0, items: 0, transactions: 0, dayCount: v.count,
      }));
    return { lossDays, lossHours, slots: foldSlots(slotAgg, allHours) };
  }, [month, dayRows, outlets, cogsSource, allHours, costAdjustments]);

  return (
    <section className="eo-panel losses">
      <div className="eo-panel-head static">
        <h3>Loss-making days &amp; hours</h3>
        <label className="eo-select">
          <span>Month</span>
          <select
            value={month ?? ""}
            onChange={(e) => onMonth(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">Select a month...</option>
            {monthsAvailable.map((m) => (
              <option key={m} value={m}>{fiscalMonthLabel(m, fiscalYear)}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="eo-panel-body">
        {month == null && (
          <p className="eo-muted">Pick a month to see which days and hours ran at a loss.</p>
        )}
        {month != null && loading && <p className="eo-muted">Loading...</p>}
        {month != null && !loading && losses && (
          <>
            <h4 className="eo-h4">
              Profit by hour, all outlets <span className="eo-h4-sub">bars below the line lose money</span>
            </h4>
            <BreakevenBars
              values={losses.slots.map((s) => s.profit)}
              labels={losses.slots.map((s) => foldedSlotLabel(s.slot))}
              height={120}
            />
            <div className="eo-axis" style={{ gridTemplateColumns: `repeat(${losses.slots.length}, 1fr)` }}>
              {losses.slots.map((s) => (
                <span key={s.slot}>{s.slot < 0 ? (s.slot === -1 ? "early" : "late") : slotShortLabel(s.slot)}</span>
              ))}
            </div>

            <div className="eo-loss-cols">
              <div>
                <h4 className="eo-h4">
                  Days at a loss <span className="eo-h4-sub">{losses.lossDays.length} of them</span>
                </h4>
                {!losses.lossDays.length && <p className="eo-muted">Every day made money.</p>}
                {!!losses.lossDays.length && (
                  <div className="eo-scroll cap">
                    <table className="eo-table">
                      <thead>
                        <tr><th>Outlet</th><th>Date</th><th className="num">Sales</th><th className="num">Profit</th></tr>
                      </thead>
                      <tbody>
                        {losses.lossDays.map((d, i) => (
                          <tr key={i}>
                            <td>{d.outlet}</td>
                            <td>
                              {new Date(`${d.date}T00:00:00Z`).toLocaleDateString("en-GB", {
                                weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
                              })}
                            </td>
                            <td className="num">{money(d.net)}</td>
                            <td className="num neg">{money(d.profit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4 className="eo-h4">
                  Hours at a loss <span className="eo-h4-sub">{losses.lossHours.length} of them</span>
                </h4>
                {!losses.lossHours.length && <p className="eo-muted">Every trading hour made money.</p>}
                {!!losses.lossHours.length && (
                  <div className="eo-scroll cap">
                    <table className="eo-table">
                      <thead>
                        <tr>
                          <th>Outlet</th><th>Date</th><th>Hour</th>
                          <th className="num">Sales</th><th className="num">Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {losses.lossHours.slice(0, 400).map((h, i) => (
                          <tr key={i}>
                            <td>{h.outlet}</td>
                            <td>
                              {new Date(`${h.date}T00:00:00Z`).toLocaleDateString("en-GB", {
                                day: "numeric", month: "short", timeZone: "UTC",
                              })}
                            </td>
                            <td>{foldedSlotLabel(h.slot)}</td>
                            <td className="num">{money(h.net)}</td>
                            <td className="num neg">{money(h.profit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {losses.lossHours.length > 400 && (
                      <p className="eo-note">
                        Showing the worst 400 of {losses.lossHours.length}; the PDF carries the full list.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

